<template>
	<div>
		<MediaLibraryModal v-on:mediaInsert="insertPhoto($event)" v-on:hideMediaModal="hideMediaModal()" />

		<!-- ADA report details modal — opened from the badges next to the client name -->
		<ReportDetailsModal
			v-if="adaModalOpen && adaReport"
			:report-id="adaReport.id"
			:client-id="$route.params.id"
			@close="adaModalOpen = false"
		/>

		<div id="editClient">
			<!-- ── Page Header ─────────────────────────────────────────────── -->
			<div class="container ce-shell">
			<JuniperPageHeader
				back="/juniper/clients"
				:title="client ? client.name : ''"
				subtitle="Client management &amp; configuration"
			>
				<template v-if="client && client.id" #title-actions>
					<a
						v-if="upgradeDashboardUrl"
						class="j-action-btn j-action-btn--teal"
						:href="upgradeDashboardUrl"
						target="_blank"
						title="Login to upgrade dashboard"
					>
						<font-awesome-icon :icon="['fas', 'fingerprint']" class="me-1" />
						<span>Upgrade Dashboard</span>
					</a>
					<a
						v-if="upgradeUrl"
						class="j-action-btn j-action-btn--sky"
						:href="upgradeUrl"
						target="_blank"
						title="Visit upgrade website"
					>
						<font-awesome-icon :icon="['fas', 'globe']" class="me-1" />
						<span>Upgrade Website</span>
					</a>
				</template>
				<!-- Website / Generate Static / Dashboard now live in the shared
					header quick-actions strip on every client edit page. -->
				<template v-if="client && client.id">
					<button
						v-if="client.staging && client.status !== 'upgrade'"
						:disabled="lodingContantStats"
						@click="launchModal.show()"
						class="ce-launch-pill"
					>
						<font-awesome-icon :icon="['fas', 'paper-plane']" />
						<span>Launch</span>
					</button>
				</template>
			</JuniperPageHeader>
			</div>

			<template v-if="!loading && client && clientOptions">
				<!-- ── Map Hero — full-width banner across the whole container ── -->
				<!-- Map geometry itself is recoloured by status (live → green, staging → blue). -->
				<div class="ce-hero">
					<GmapMap
						:center="center"
						:zoom="12"
						map-type-id="roadmap"
						:options="mapOptions"
						class="ce-hero__map"
						style="width: 100%; height: 100%"
					>
						<GmapMarker :key="'client-marker'" :position="center" :draggable="false" />
					</GmapMap>
					<div class="ce-hero__fade"></div>
					<router-link v-if="mcClient" class="ce-map-overlay ce-map-overlay--top-left" :to="'\/clients\/edit\/' + mcClient.id">
						<font-awesome-icon :icon="['fas', 'link']" class="me-1" /> MC2 Info
					</router-link>
					<div v-if="mcClient && mcClient.municipality && mcClient.municipality.population" class="ce-map-overlay ce-map-overlay--top-right">
						<font-awesome-icon :icon="['fas', 'users']" class="me-1" />{{ Number(mcClient.municipality.population).toLocaleString() }}
					</div>
				</div>

				<div class="container ce-shell ce-body">
				<!-- ── Client Details Card (tucks up into the map hero) ──────── -->
				<div class="ce-info-grid">

					<!-- Client Details Card -->
					<div class="ce-card ce-card--lifted" :class="'ce-card--border-' + mapStatusKey">
						<button
							v-if="authUser.email && authUser.email.includes('srdjan') && (client.status !== 'live' || client.staging)"
							class="ce-card-delete"
							@click="deleteClient()"
							title="Delete client permanently"
						>
							<font-awesome-icon :icon="['fas', 'trash']" />
						</button>
						<div class="ce-client-hero">
							<div class="ce-hero-left">
								<div class="ce-logo-wrap">
									<a role="button" @click="removeLogo()" v-if="client.logo" class="ce-logo-remove">
										<font-awesome-icon :icon="['fas', 'trash']" />
									</a>
									<img
										v-if="client.logo"
										@click="showMediaModal()"
										class="ce-logo-img cursor-pointer"
										:src="client.logo"
										:alt="client.name"
										title="For logo use .png if possible"
									/>
									<button v-else class="ce-logo-placeholder" @click="showMediaModal()">+ Logo</button>
								</div>
								<div>
									<div class="ce-name-row">
										<h3
											class="ce-client-name"
											:class="'ce-client-name--' + mapStatusKey"
											role="button"
											title="Click to edit client metadata"
											@click="metaModal && metaModal.show()"
										>
											{{ client.name }}
											<font-awesome-icon :icon="['fas', 'pen']" class="ce-edit-icon" />
										</h3>
										<button
											v-if="adaReport"
											type="button"
											class="ce-ada-badges"
											@click.stop="adaModalOpen = true"
										>
											<span
												v-if="adaReport.poor_pages > 0"
												class="ce-ada-badge ce-ada-badge--error"
												data-bs-toggle="tooltip"
												data-bs-placement="top"
												data-bs-html="true"
												:title="adaErrorTooltip(adaReport)"
											>{{ adaReport.poor_pages }}</span>
											<span
												v-else
												class="ce-ada-badge ce-ada-badge--ok"
												data-bs-toggle="tooltip"
												data-bs-placement="top"
												data-bs-html="true"
												:title="adaOkTooltip(adaReport)"
											><i class="fas fa-check"></i></span>
											<span
												v-if="adaReport.warn_pages > 0"
												class="ce-ada-badge ce-ada-badge--warning"
												data-bs-toggle="tooltip"
												data-bs-placement="top"
												data-bs-html="true"
												:title="adaWarnTooltip(adaReport)"
											>{{ adaReport.warn_pages }}</span>
										</button>
									</div>
									<template v-if="mcClient">
										<p class="ce-meta">{{ mcClient.county }}, {{ mcClient.state }}</p>
									</template>
								</div>
							</div>
							<div class="ce-hero-right">
								<span
									v-if="lastActivity"
									class="ce-feat ce-feat--activity"
									data-bs-toggle="tooltip"
									data-bs-placement="top"
									data-bs-html="true"
									:title="lastActivityTooltip"
								>
									<font-awesome-icon :icon="['fas', 'user-edit']" />
								</span>
								<span
									v-if="client.dashboard"
									class="ce-dash-badge"
									:class="client.dashboard === 'unified' ? 'ce-dash-badge--unified' : 'ce-dash-badge--old'"
									data-bs-toggle="tooltip"
									data-bs-placement="top"
									:title="client.dashboard === 'unified' ? 'Unified Dashboard — modern HeyGov-integrated experience' : 'Town Web Dashboard — classic interface'"
								>
									<font-awesome-icon :icon="['fas', client.dashboard === 'unified' ? 'bolt' : 'th-large']" class="me-1" />{{ client.dashboard === 'unified' ? 'Unified' : 'Old' }}
								</span>
								<span v-if="mcFeatures.length" class="ce-features">
									<span
										v-for="feat in mcFeatures"
										:key="feat.key"
										class="ce-feat"
										:class="feat.cls"
										data-bs-toggle="tooltip"
										data-bs-placement="top"
										data-bs-html="true"
										:title="feat.tip"
									>
										<font-awesome-icon :icon="feat.icon" />
									</span>
								</span>
								<router-link v-if="clientOptions.theme_active" class="ce-theme-badge" :to="'/juniper/clients/edit/' + client.id + '/customizer'" title="Open Customizer">
									<font-awesome-icon :icon="['fas', 'palette']" class="me-1" />{{ clientOptions.theme_active }}<template v-if="clientOptions.theme_active == 'default'"> (Maverick)</template>
								</router-link>
								<span
									v-if="projectManagerName"
									class="ce-pm-badge"
									data-bs-toggle="tooltip"
									data-bs-placement="top"
									title="Project Manager"
								>
									<font-awesome-icon :icon="['fas', 'user-tie']" class="me-1" />{{ projectManagerName }}
								</span>
								<span class="ce-status-pill ce-status-pill--green" v-if="!client.staging && !client.maintenance_mode">
									<font-awesome-icon :icon="['fas', 'circle']" class="me-1 ce-live-dot" /> Live
								</span>
								<span class="ce-status-pill ce-status-pill--purple" v-else-if="client.status === 'upgrade' && !client.maintenance_mode">
									<font-awesome-icon :icon="['fas', 'sync']" class="me-1" /> Upgrade
								</span>
								<span class="ce-status-pill ce-status-pill--blue" v-else-if="client.staging && !client.maintenance_mode">
									<font-awesome-icon :icon="['fas', 'flask']" class="me-1" /> Staging
								</span>
								<span class="ce-status-pill ce-status-pill--orange" v-else-if="client.maintenance_mode">
									<font-awesome-icon :icon="['fas', 'hard-hat']" class="me-1" /> Maintenance
								</span>
							</div>
						</div>

						<div class="ce-card-body">
							<div class="ce-card-info">
								<div class="ce-links-row">
									<a v-if="!isUpgradeClient && client.url" class="ce-link ce-link--primary" :href="client.url" target="_blank" :title="client.url">
										<span class="ce-link__ic"><font-awesome-icon :icon="['fas', 'globe']" /></span>
										<span class="ce-link__url">{{ stripDeleteProtocol(client.url) }}</span>
									</a>
									<div v-if="client.staging_url" class="ce-staging-row">
										<a class="ce-link ce-link--muted" :href="client.staging_url" target="_blank" :title="client.staging_url">
											<span class="ce-link__ic"><font-awesome-icon :icon="['fas', 'flask']" /></span>
											<span class="ce-link__url">{{ stripDeleteProtocol(client.staging_url) }}</span>
										</a>
										<button
											v-if="canMigrateStagingUrl"
											type="button"
											class="ce-migrate-btn"
											:disabled="migratingStagingUrl"
											@click="migrateStagingUrl"
											title="Provision a new jpNNNNstg.townweb.site alias. The current townweb.com alias stays in place."
										>
											<template v-if="!migratingStagingUrl">Migrate</template>
											<template v-else>…</template>
										</button>
									</div>
								</div>
								<router-link class="ce-backup-row" :to="'/juniper/clients/edit/' + client.id + '/deploy'" title="View deployments">
									<span class="ce-backup-icon"><font-awesome-icon :icon="['fas', 'hammer']" /></span>
									<span v-if="loadingLastStatic" class="spinner-border spinner-border-sm text-secondary" role="status"></span>
									<span v-else>Last static: <b>{{ lastStaticRelative }}</b></span>
								</router-link>
							</div>

							<div class="ce-stats-row">
								<router-link class="ce-stat-chip ce-stat-chip--link" title="All pages (incl. deleted)" :to="'/juniper/clients/edit/' + client.id + '/pages'">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--blue" :icon="['fas', 'file-alt']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalPages" /></span>
										<span class="ce-stat-chip-label">Pages</span>
									</span>
								</router-link>
								<div class="ce-stat-chip" title="Meetings">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--teal" :icon="['fas', 'gavel']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalMeetings" /></span>
										<span class="ce-stat-chip-label">Meetings</span>
									</span>
								</div>
								<div class="ce-stat-chip" title="Events">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--orange" :icon="['fas', 'calendar-day']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalEvents" /></span>
										<span class="ce-stat-chip-label">Events</span>
									</span>
								</div>
								<router-link class="ce-stat-chip ce-stat-chip--link" title="All news & notices (incl. deleted)" :to="'/juniper/clients/edit/' + client.id + '/news-notices'">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--pink" :icon="['fas', 'newspaper']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalNews" /></span>
										<span class="ce-stat-chip-label">News</span>
									</span>
								</router-link>
								<div class="ce-stat-chip" title="Subscribers">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--red" :icon="['fas', 'users']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalSubscribers" /></span>
										<span class="ce-stat-chip-label">Subscribers</span>
									</span>
								</div>
								<div class="ce-stat-chip" title="Departments">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--green" :icon="['fas', 'sitemap']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalDepartments" /></span>
										<span class="ce-stat-chip-label">Depts</span>
									</span>
								</div>
								<div class="ce-stat-chip" title="Landing Pages">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--purple" :icon="['fas', 'layer-group']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalLandingPages" /></span>
										<span class="ce-stat-chip-label">Landings</span>
									</span>
								</div>
								<div class="ce-stat-chip" title="Business Directory">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--indigo" :icon="['fas', 'address-book']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalBusinessDirectory" /></span>
										<span class="ce-stat-chip-label ce-stat-chip-label--multi">Business Directory</span>
									</span>
								</div>
								<div class="ce-stat-chip" title="Files">
									<font-awesome-icon class="ce-stat-chip__bg ce-stat-chip__bg--cyan" :icon="['fas', 'folder']" />
									<span class="ce-stat-chip__body">
										<span class="ce-stat-chip-value"><CountUp :value="totalFiles" /></span>
										<span class="ce-stat-chip-label">Files</span>
									</span>
								</div>
							</div>
						</div>

					</div>
				</div>

				<div class="ce-card ce-sections-card">
				<div class="ce-sections">
				<div class="ce-section">
				<div class="ce-section-header">
					<div>
						<div class="ce-section-title">Content &amp; Design</div>
						<div class="ce-section-subtitle">Build and style the pages, menus and widgets</div>
					</div>
				</div>
				<div class="ce-actions-grid">
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/customizer'">
						<span class="ce-action-icon ce-action-icon--purple"><font-awesome-icon :icon="['fas', 'palette']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Customizer</span>
							<span class="ce-action-desc">Theme & branding</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/menus'">
						<span class="ce-action-icon ce-action-icon--teal"><font-awesome-icon :icon="['fas', 'bars']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Menus</span>
							<span class="ce-action-desc">Navigation structure</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/landing-pages'">
						<span class="ce-action-icon ce-action-icon--pink"><font-awesome-icon :icon="['fas', 'layer-group']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Landing Pages</span>
							<span class="ce-action-desc">Campaign pages</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/widgets'">
						<span class="ce-action-icon ce-action-icon--indigo"><font-awesome-icon :icon="['fas', 'cube']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Widgets</span>
							<span class="ce-action-desc">Reusable blocks</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/departments'">
						<span class="ce-action-icon ce-action-icon--green"><font-awesome-icon :icon="['fas', 'cubes']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Dept. Widgets</span>
							<span class="ce-action-desc">Department blocks</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
				</div>
				</div>
				<div class="ce-section">
				<div class="ce-section-header">
					<div>
						<div class="ce-section-title">Configuration</div>
						<div class="ce-section-subtitle">Site settings, SEO and URL handling</div>
					</div>
				</div>
				<div class="ce-actions-grid">
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/settings'">
						<span class="ce-action-icon ce-action-icon--blue"><font-awesome-icon :icon="['fas', 'cog']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Settings</span>
							<span class="ce-action-desc">General configuration</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/seo'">
						<span class="ce-action-icon ce-action-icon--orange"><font-awesome-icon :icon="['fas', 'chart-line']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">SEO</span>
							<span class="ce-action-desc">Meta tags & sitemaps</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/redirections'">
						<span class="ce-action-icon ce-action-icon--indigo"><font-awesome-icon :icon="['fas', 'route']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Redirections</span>
							<span class="ce-action-desc">URL redirects</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/search-replace'">
						<span class="ce-action-icon ce-action-icon--teal"><font-awesome-icon :icon="['fas', 'search']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Search &amp; Replace</span>
							<span class="ce-action-desc">Bulk content edits</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
				</div>
				</div>
				<div class="ce-section">
				<div class="ce-section-header">
					<div>
						<div class="ce-section-title">Tools &amp; Insights</div>
						<div class="ce-section-subtitle">Audits, search and activity history</div>
					</div>
				</div>
				<div class="ce-actions-grid">
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/accessibility'">
						<span class="ce-action-icon ce-action-icon--cyan"><font-awesome-icon :icon="['fas', 'universal-access']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Accessibility</span>
							<span class="ce-action-desc">Audits &amp; scan settings</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<!--
						ADA Auditor card — hidden, replaced by Accessibility above. The
						ClientAdaAuditor view and its route (/ada-auditor) are kept and
						still reachable by URL.
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/ada-auditor'">
						<span class="ce-action-icon ce-action-icon--cyan"><font-awesome-icon :icon="['fas', 'universal-access']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">ADA Auditor</span>
							<span class="ce-action-desc">Accessibility audit</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					-->
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/ai-search'">
						<span class="ce-action-icon ce-action-icon--green"><font-awesome-icon :icon="['fas', 'brain']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">AI Search</span>
							<span class="ce-action-desc">Search index</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/analytics'">
						<span class="ce-action-icon ce-action-icon--blue"><font-awesome-icon :icon="['fas', 'chart-line']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Analytics</span>
							<span class="ce-action-desc">Page views</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/activities'">
						<span class="ce-action-icon ce-action-icon--orange"><font-awesome-icon :icon="['fas', 'history']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Activities</span>
							<span class="ce-action-desc">Audit log</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
				</div>
				</div>
				<div class="ce-section">
				<div class="ce-section-header">
					<div>
						<div class="ce-section-title">Operations</div>
						<div class="ce-section-subtitle">Publishing, imports, upgrades and access</div>
					</div>
				</div>
				<div class="ce-actions-grid">
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/deploy'">
						<span class="ce-action-icon ce-action-icon--purple"><font-awesome-icon :icon="['fas', 'rocket']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Deploy</span>
							<span class="ce-action-desc">Static builds</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<router-link
						v-if="userHasAllNeededJuniperPermissions(['wp_import'])"
						class="ce-action-card"
						:class="{ 'ce-action-card--disabled': !client.staging }"
						:to="'/juniper/clients/edit/' + client.id + '/wp-import'"
						:title="!client.staging ? 'Staging only' : ''"
					>
						<span class="ce-action-icon ce-action-icon--gray"><font-awesome-icon :icon="['fas', 'file-import']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">WP Import</span>
							<span class="ce-action-desc">Import from WordPress</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
					<JuniperClientUpgradeCard
						v-if="client && client.id"
						:client-id="client.id"
						@upgrade-changed="onUpgradeChanged"
						@upgrade-status="onUpgradeStatus"
					/>
					<router-link class="ce-action-card" :to="'/juniper/clients/edit/' + client.id + '/users'">
						<span class="ce-action-icon ce-action-icon--blue"><font-awesome-icon :icon="['fas', 'users']" /></span>
						<span class="ce-action-card__text">
							<span class="ce-action-label">Users</span>
							<span class="ce-action-desc">Access & roles</span>
						</span>
						<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
					</router-link>
				</div>
				</div>
				</div>
				</div>
				</div>

			</template>

			<!-- ── Skeleton Loader ────────────────────────────────────────── -->
			<div v-if="loading" class="ce-skeleton" aria-busy="true" aria-live="polite">
				<!-- Map Hero Skeleton (full width) -->
				<div class="ce-hero ce-hero--skeleton ce-skeleton-shimmer"></div>

				<div class="container ce-shell ce-body">
					<!-- Client Details Card Skeleton -->
					<div class="ce-card ce-card--lifted ce-skeleton__card">
						<div class="ce-skeleton__hero">
							<div class="ce-skeleton__hero-left">
								<div class="ce-skeleton__logo ce-skeleton-shimmer"></div>
								<div class="ce-skeleton__hero-text">
									<div class="ce-skeleton-line ce-skeleton-line--title ce-skeleton-shimmer"></div>
									<div class="ce-skeleton-line ce-skeleton-line--sub ce-skeleton-shimmer"></div>
								</div>
							</div>
							<div class="ce-skeleton__hero-right">
								<div class="ce-skeleton__pill ce-skeleton-shimmer"></div>
								<div class="ce-skeleton__badge ce-skeleton-shimmer"></div>
							</div>
						</div>
						<div class="ce-skeleton__links">
							<div class="ce-skeleton-line ce-skeleton-line--link ce-skeleton-shimmer"></div>
							<div class="ce-skeleton-line ce-skeleton-line--link ce-skeleton-shimmer"></div>
						</div>
						<div class="ce-skeleton__stats">
							<div v-for="i in 8" :key="'stat-' + i" class="ce-skeleton__stat ce-skeleton-shimmer"></div>
						</div>
						<div class="ce-skeleton-line ce-skeleton-line--backup ce-skeleton-shimmer"></div>
					</div>

					<!-- Section Header Skeleton -->
				<div class="ce-section-header">
					<div>
						<div class="ce-skeleton-line ce-skeleton-line--section-title ce-skeleton-shimmer"></div>
						<div class="ce-skeleton-line ce-skeleton-line--section-sub ce-skeleton-shimmer"></div>
					</div>
				</div>

				<!-- Actions Grid Skeleton (Customizing) -->
				<div class="ce-actions-grid">
					<div v-for="i in 8" :key="'cust-' + i" class="ce-action-card ce-skeleton__action">
						<div class="ce-skeleton__action-icon ce-skeleton-shimmer"></div>
						<div class="ce-skeleton-line ce-skeleton-line--action ce-skeleton-shimmer"></div>
					</div>
				</div>

				<!-- Section Header Skeleton -->
				<div class="ce-section-header">
					<div>
						<div class="ce-skeleton-line ce-skeleton-line--section-title ce-skeleton-shimmer"></div>
						<div class="ce-skeleton-line ce-skeleton-line--section-sub ce-skeleton-shimmer"></div>
					</div>
				</div>

				<!-- Actions Grid Skeleton (Advanced) -->
				<div class="ce-actions-grid">
					<div v-for="i in 7" :key="'adv-' + i" class="ce-action-card ce-skeleton__action">
						<div class="ce-skeleton__action-icon ce-skeleton-shimmer"></div>
						<div class="ce-skeleton-line ce-skeleton-line--action ce-skeleton-shimmer"></div>
					</div>
				</div>
				</div>
			</div>
		</div>

		<!-- ── Setup alert toasts (bottom-right, auto-dismiss after 15s) ──── -->
		<div class="ce-toast-stack">
			<transition name="ce-toast">
				<div v-if="showErrorAlerts && errorAlerts.length > 0" class="ce-alert-panel ce-alert-panel--danger ce-toast" role="alert">
					<div class="ce-alert-panel__header">
						<div class="ce-alert-panel__title-wrap">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
							<span class="ce-alert-panel__title">Critical setup issues ({{ errorAlerts.length }})</span>
							<router-link class="ce-alert-panel__settings-link" :to="clientSettingsRoute">Open Settings</router-link>
						</div>
						<button class="ce-alert-panel__close" type="button" @click="dismissErrorAlerts()" aria-label="Close">
							<span aria-hidden="true">&times;</span>
						</button>
					</div>
					<ul class="ce-alert-panel__list">
						<li v-for="alert in errorAlerts" :key="alert.id">
							<strong>{{ alert.title }}</strong>
							<span>{{ alert.description }}</span>
						</li>
					</ul>
				</div>
			</transition>
			<transition name="ce-toast">
				<div v-if="showWarningAlerts && warningAlerts.length > 0" class="ce-alert-panel ce-alert-panel--warning ce-toast" role="alert">
					<div class="ce-alert-panel__header">
						<div class="ce-alert-panel__title-wrap">
							<font-awesome-icon :icon="['fas', 'circle-info']" />
							<span class="ce-alert-panel__title">Recommended updates ({{ warningAlerts.length }})</span>
							<router-link class="ce-alert-panel__settings-link" :to="clientSettingsRoute">Open Settings</router-link>
						</div>
						<button class="ce-alert-panel__close" type="button" @click="dismissWarningAlerts()" aria-label="Close">
							<span aria-hidden="true">&times;</span>
						</button>
					</div>
					<ul class="ce-alert-panel__list">
						<li v-for="alert in warningAlerts" :key="alert.id">
							<strong>{{ alert.title }}</strong>
							<span>{{ alert.description }}</span>
						</li>
					</ul>
				</div>
			</transition>
		</div>

		<!-- ── Modal Export Website Data ─────────────────────────────────── -->
		<div class="modal fade" id="exportModal" ref="exportModal" aria-labelledby="exportModalLabel">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="exportModalLabel">Export Data</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="bd-callout bd-callout-info">
							<h5 id="conveying-meaning-to-assistive-technologies">
								Exporting Client Data
							</h5>
							<p>
								Here you can export client data for backup or for anything else. You can choose witch data you want to export (customizer, pages, events ...).
								Purpose for this is to have it stored somewhere if need it, and for implementing same data in new website. Data will be exported in
								<code>.json</code> file.
							</p>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.customizer" id="customizerData" />
							<label class="form-check-label" for="customizerData">Customizer Data</label>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.pages" id="pagesData" checked />
							<label class="form-check-label" for="pagesData">Pages</label>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.news" id="newsData" checked />
							<label class="form-check-label" for="newsData">News and Notices</label>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.posts" id="postsData" checked />
							<label class="form-check-label" for="postsData">Posts</label>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.departments" id="departmentsData" checked />
							<label class="form-check-label" for="departmentsData">Departments</label>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.faqs" id="faqsData" checked />
							<label class="form-check-label" for="faqsData">Faqs</label>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.events" id="eventsData" checked />
							<label class="form-check-label" for="eventsData">Events</label>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.meetings" id="meetingData" checked />
							<label class="form-check-label" for="meetingData">Meetings</label>
						</div>
						<div class="form-check">
							<input class="form-check-input" type="checkbox" v-model="exportData.subscribers" id="subscribersData" checked />
							<label class="form-check-label" for="subscribersData">Subscribers</label>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button type="button" class="btn btn-primary" disabled>Coming Soon 😉</button>
					</div>
				</div>
			</div>
		</div>

		<!-- ── Modal Launch Website ───────────────────────────────────────── -->
		<div class="modal fade" ref="launchModal" id="launchModal" aria-labelledby="launchModalLabel" data-bs-backdrop="static">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<div class="d-flex align-items-center">
							<div class="ce-modal-icon ce-modal-icon--green me-2">
								<font-awesome-icon :icon="['fas', 'rocket']" />
							</div>
							<div>
								<h5 class="modal-title mb-0" id="launchWebsiteModalLabel">Launch website?</h5>
								<div class="text-muted small">Take this staging site live on the production URL.</div>
							</div>
						</div>
						<button type="button" :disabled="launchLoading" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="alert alert-success bg-success-lighter text-black" role="alert">
							<h4 class="alert-heading text-primary">We are ready!?</h4>
							In order to launch the website live, the following actions must be performed:
							<ul>
								<li>All urls must be changed from staging to live url (don't worry it goes automatically)</li>
								<li>
									The DNS records must be checked to see if they point to the correct IP address of the server
									<code class="bg-warning-light p-1">5.161.88.82</code>
								</li>
								<li>.... No more actions</li>
							</ul>
							<hr />
							<p class="mb-0">The website cannot be returned to staging mode after going live. If you really need it, contact the Dev team.</p>
						</div>
						<div class="alert alert-warning bg-warning-lighter text-black" role="alert">
							<p class="p-0 m-0">
								👋 Releasing the site live will not change the links in menu items if absolute paths were used. Absolute paths should only be used when linking to
								external websites
							</p>
						</div>
						<div v-if="itsFriday" class="alert alert-warning bg-warning-lighter text-black" role="alert">
							<h5 class="text-black">Why Avoid Launching Websites on Fridays?</h5>
							<p class="p-0 m-0">
								Launching a website on a Friday might seem convenient, but it can lead to unexpected challenges. Fridays often leave limited time to address
								post-launch issues, as many team members and technical support staff might be unavailable over the weekend. Bugs, downtime, or user feedback can go
								unresolved, impacting user experience and your brand's reputation. Waiting until Monday offers more benefits. Your team is fully available to
								monitor performance, troubleshoot, and make adjustments in real-time. This proactive approach ensures a smoother launch, minimizing risks and
								maximizing success.
							</p>
						</div>
						<div class="alert alert-info bg-info-lighter text-black" role="alert">
							<div class="d-flex justify-content-between align-items-center mb-2">
								<span class="fw-bold">Pages:</span>
								<span>There is <code class="bg-success-lighter p-1">{{ totalPages }}</code> pages
									<template v-if="totalPages > 0">
										and <code class="bg-warning-light p-1">{{ totalPagesStagingUrl }}</code> contains staging url
										<font-awesome-icon v-if="pagesReplacingLoading" :icon="['fas', 'spinner']" class="ms-1 fa-spin" />
										<font-awesome-icon v-if="pagesReplacingDone" class="text-success" :icon="['fas', 'check']" />
									</template>
								</span>
							</div>
							<div class="d-flex justify-content-between align-items-center mb-2">
								<span class="fw-bold">News and Notices:</span>
								<span>There is <code class="bg-success-lighter p-1">{{ totalNews }}</code> news and notices
									<template v-if="totalNews > 0">
										and <code class="bg-warning-light p-1">{{ totalNewsStagignUrl }}</code> contains staging url
										<font-awesome-icon v-if="newsReplacingLoading" :icon="['fas', 'spinner']" class="ms-1 fa-spin" />
										<font-awesome-icon v-if="newsReplacingDone" class="text-success" :icon="['fas', 'check']" />
									</template>
								</span>
							</div>
							<div class="d-flex justify-content-between align-items-center mb-2">
								<span class="fw-bold">Departments:</span>
								<span>There is <code class="bg-success-lighter p-1">{{ totalDepartments }}</code> departments
									<template v-if="totalDepartments > 0">
										and <code class="bg-warning-light p-1">{{ totalDepartmentsStagingUrl }}</code> contains staging url
										<font-awesome-icon v-if="departmentsReplacingLoading" :icon="['fas', 'spinner']" class="ms-1 fa-spin" />
										<font-awesome-icon v-if="departmentsReplacingDone" class="text-success" :icon="['fas', 'check']" />
									</template>
								</span>
							</div>
							<div class="d-flex justify-content-between align-items-center mb-2">
								<span class="fw-bold">Events:</span>
								<span>There is <code class="bg-success-lighter p-1">{{ totalEvents }}</code> events</span>
							</div>
							<div class="d-flex justify-content-between align-items-center mb-2">
								<span class="fw-bold">Meetings:</span>
								<span>There is <code class="bg-success-lighter p-1">{{ totalMeetings }}</code> meetings</span>
							</div>
							<div class="d-flex justify-content-between align-items-center mb-2">
								<span class="fw-bold">Static generation:</span>
								<span>Regenerate static content
									<font-awesome-icon v-if="contentGeneratingLoading" :icon="['fas', 'spinner']" class="ms-1 fa-spin" />
									<font-awesome-icon v-if="contentGeneratingDone" class="text-success" :icon="['fas', 'check']" />
								</span>
							</div>
							<div v-if="adaLaunchApplies" class="d-flex justify-content-between align-items-center mb-2">
								<span class="fw-bold">ADA errors:</span>
								<span v-if="adaReport && adaReport.errors_by_source">
									<template v-for="(b, i) in adaLaunchBuckets">
										{{ b.label }}
										<code :key="b.key" :class="b.blocking ? 'bg-danger-lighter' : 'bg-success-lighter'" class="p-1">{{ b.count }}</code>{{ i === adaLaunchBuckets.length - 1 ? '' : ',' }}
									</template>
									errors
									<font-awesome-icon v-if="!adaLaunchBlocked" class="text-success" :icon="['fas', 'check']" />
								</span>
								<span v-else :class="adaLaunchBlockers.no_audit ? 'text-danger' : 'text-muted'">No finished ADA audit</span>
							</div>
						</div>
						<div v-if="launchLoading" class="alert alert-warning">
							<font-awesome-icon :icon="['fas', 'spinner']" :class="['fa-spin me-1']" /> We do everything necessary before the site goes live. Please wait and do not
							go from this page until everything is finished!
						</div>
					</div>
					<div class="modal-footer">
						<span v-if="adaLaunchBlockReason" class="me-auto small text-danger">
							<font-awesome-icon :icon="['fas', 'universal-access']" class="me-1" />{{ adaLaunchBlockReason }}
						</span>
						<button :disabled="launchLoading" type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button v-if="!itsFriday" @click="launchWebsite()" :disabled="launchLoading || adaLaunchBlocked" type="button" class="btn btn-primary">
							Launch! <font-awesome-icon :icon="[launchLoading ? 'fas' : 'fas', launchLoading ? 'spinner' : 'paper-plane']" :class="[launchLoading ? 'fa-spin' : '']" />
						</button>
						<button v-else @click="launchWebsite()" :disabled="launchLoading || adaLaunchBlocked" type="button" class="btn btn-danger">
							Launch! <font-awesome-icon :icon="[launchLoading ? 'fas' : 'fas', launchLoading ? 'spinner' : 'paper-plane']" :class="[launchLoading ? 'fa-spin' : '']" />
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- ── Modal Delete Client ───────────────────────────────────────── -->
		<div class="modal fade" ref="deleteModal" id="deleteModal" aria-labelledby="deleteModalLabel" data-bs-backdrop="static">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title mb-0" id="deleteModalLabel">
								Permanently delete {{ isDeleteUpgrade ? 'upgrade copy' : 'client' }}?
							</h5>
							<div class="text-muted small">
								<template v-if="isDeleteUpgrade">The upgrade copy will be removed — the live client is untouched.</template>
								<template v-else>The client and all its content will be removed from the database.</template>
							</div>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="deleteLoading"></button>
					</div>

					<div class="modal-body">
						<div class="migrate-summary">
							<div class="migrate-summary__row">
								<span class="migrate-summary__label">{{ isDeleteUpgrade ? 'Upgrade name' : 'Client name' }}</span>
								<span class="migrate-summary__value">{{ client.name || '—' }}</span>
							</div>
							<div class="migrate-summary__row">
								<span class="migrate-summary__label">Juniper ID</span>
								<span class="migrate-summary__value"><code>{{ client.id }}</code></span>
							</div>
							<div class="migrate-summary__row" v-if="client.status">
								<span class="migrate-summary__label">Status</span>
								<span class="migrate-summary__value text-capitalize">{{ client.status }}</span>
							</div>
							<div class="migrate-summary__row" v-if="deleteUrl">
								<span class="migrate-summary__label">{{ client.staging ? 'Staging URL' : 'Live URL' }}</span>
								<span class="migrate-summary__value">
									<a :href="ensureDeleteUrl(deleteUrl)" target="_blank">{{ stripDeleteProtocol(deleteUrl) }}</a>
								</span>
							</div>
						</div>

						<h6 class="text-uppercase text-muted small fw-bold mt-3 mb-2">Content that will be deleted</h6>
						<ul class="migrate-steps small mb-0">
							<li><strong>{{ totalPages }}</strong> pages, <strong>{{ totalNews }}</strong> news, <strong>{{ totalDepartments }}</strong> departments</li>
							<li><strong>{{ totalEvents }}</strong> events, <strong>{{ totalMeetings }}</strong> meetings</li>
							<li>FAQs, files, options and any related data</li>
						</ul>

						<div class="alert alert-danger d-flex align-items-start mt-3 mb-0 small">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-2 mt-1" />
							<div>
								<strong>This action cannot be undone.</strong>
								Plesk subscriptions and DNS records are not affected.
							</div>
						</div>

						<div class="mt-3" v-if="client.name">
							<label class="form-label small fw-semibold mb-1">Type <code>{{ client.name }}</code> to confirm</label>
							<input
								type="text"
								class="form-control"
								v-model="deleteConfirmText"
								:disabled="deleteLoading"
								:placeholder="client.name"
								autocomplete="off"
							/>
						</div>
					</div>

					<div class="modal-footer">
						<div class="me-auto small text-muted" v-if="deleteLoading">
							<span class="spinner-border spinner-border-sm me-2" role="status"></span>
							Deleting…
						</div>
						<button type="button" class="btn btn-light" data-bs-dismiss="modal" :disabled="deleteLoading">Cancel</button>
						<button type="button" class="btn btn-danger" @click="confirmDelete()" :disabled="deleteLoading || !canConfirmDelete">
							<font-awesome-icon :icon="['fas', 'trash']" class="me-1" />
							Delete
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- ── Modal — Migrate Staging URL ───────────────────────────────── -->
		<div class="modal fade" ref="migrateStagingModal" id="migrateStagingModal" aria-labelledby="migrateStagingModalLabel" data-bs-backdrop="static">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<div class="d-flex align-items-center">
							<div class="me-2 text-primary">
								<font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
							</div>
							<div>
								<h5 class="modal-title mb-0" id="migrateStagingModalLabel">Migrate staging URL?</h5>
								<div class="text-muted small">Move this client to the new <code>townweb.site</code> staging zone.</div>
							</div>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="migratingStagingUrl"></button>
					</div>

					<div class="modal-body">
						<div class="migrate-summary">
							<div class="migrate-summary__row">
								<span class="migrate-summary__label">Current staging URL</span>
								<span class="migrate-summary__value"><code>{{ client.staging_url || '—' }}</code></span>
							</div>
							<div class="migrate-summary__row">
								<span class="migrate-summary__label">New staging URL</span>
								<span class="migrate-summary__value"><code>{{ newStagingUrlPreview }}</code></span>
							</div>
						</div>

						<h6 class="text-uppercase text-muted small fw-bold mt-3 mb-2">What will happen</h6>
						<ul class="migrate-steps small mb-0">
							<li>A new DNS <code>A</code> record will be created on Cloudflare in the <code>townweb.site</code> zone.</li>
							<li>A new domain alias will be added on the existing Plesk subscription for <code>{{ client.url || '—' }}</code>.</li>
							<li>The <code>staging_url</code> stored on this Juniper client will be updated to the new URL.</li>
						</ul>

						<div class="alert alert-info d-flex align-items-start mt-3 mb-0 small">
							<font-awesome-icon :icon="['fas', 'circle-info']" class="me-2 mt-1" />
							<div>
								<strong>Old URL keeps working.</strong>
								The current <code>townweb.com</code> alias and DNS record are <em>not</em> removed — existing bookmarks and CMS content references continue to work.
							</div>
						</div>
					</div>

					<div class="modal-footer">
						<div class="me-auto small text-muted" v-if="migratingStagingUrl">
							<span class="spinner-border spinner-border-sm me-2" role="status"></span>
							Provisioning…
						</div>
						<button type="button" class="btn btn-light" data-bs-dismiss="modal" :disabled="migratingStagingUrl">Cancel</button>
						<button type="button" class="btn btn-primary" @click="confirmMigrateStagingUrl()" :disabled="migratingStagingUrl">
							<font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="me-1" />
							Migrate
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- ── Offcanvas — Dev Client Edit ───────────────────────────────── -->
		<!-- ── Modal — Client metadata ───────────────────────────────────── -->
		<div class="modal fade" ref="metaModal" id="metaModal" aria-labelledby="metaModalLabel" data-bs-backdrop="static">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title mb-0" id="metaModalLabel">{{ client.name }}</h5>
							<div class="text-muted small">Client metadata</div>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="mb-3">
							<label class="form-label small fw-semibold mb-1" for="projectManager">Project Manager</label>
							<select
								id="projectManager"
								class="form-select"
								:value="client.project_manager_id || ''"
								@change="setProjectManager($event.target.value)"
							>
								<option value="">—</option>
								<option v-for="member in projectManagers" :key="member.id" :value="member.id">{{ member.name }}</option>
								<!-- Credited to someone outside the current whitelist — keep it
								selectable so saving doesn't silently drop the credit. -->
								<option v-if="projectManagerUnlisted" :value="client.project_manager_id">
									{{ client.project_manager_name || 'User #' + client.project_manager_id }} (unlisted)
								</option>
							</select>
						</div>
						<div class="mb-3">
							<label class="form-label small fw-semibold mb-1" for="status">Status</label>
							<select id="status" class="form-select" v-model="client.status">
								<option value="live">Live</option>
								<option value="preparing">Preparing</option>
								<option value="development">Development</option>
								<option value="abandoned">Abandoned</option>
								<option value="left">Left</option>
								<option value="upgrade">Upgrade</option>
							</select>
						</div>
						<div class="mb-3">
							<label class="form-label small fw-semibold mb-1" for="type">Type</label>
							<select id="type" class="form-select" v-model="client.type">
								<option value="municipal">Municipal</option>
								<option value="business">Bussiness</option>
								<option value="internal">Internal</option>
							</select>
						</div>
						<div class="mb-3">
							<label class="form-label small fw-semibold mb-1" for="clientUrl">Domain</label>
							<input id="clientUrl" type="url" class="form-control" v-model.trim="client.url" placeholder="https://example.org" />
							<div v-if="urlChanged" class="form-check mt-2">
								<input id="renamePleskSub" class="form-check-input" type="checkbox" v-model="renamePleskSubscription" />
								<label class="form-check-label small" for="renamePleskSub">
									Rename Plesk subscription too
									<span class="d-block text-muted">
										Renames the webspace {{ stripDeleteProtocol(originalUrl) }} &rarr; {{ stripDeleteProtocol(client.url) }} on Plesk.
										Staging alias, FTP and files stay in place. DNS (Cloudflare) and SSL for the new domain are <strong>not</strong> handled automatically.
									</span>
								</label>
							</div>
						</div>
						<div class="mb-0">
							<label class="form-label small fw-semibold mb-1">Launched At</label>
							<input type="date" class="form-control" :value="formatDate(client.launched_at)" @input="client.launched_at = $event.target.value" />
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-light" data-bs-dismiss="modal" :disabled="updatingClient">Cancel</button>
						<button type="button" class="btn btn-primary" :disabled="updatingClient" @click="updateClient()">
							<span v-if="updatingClient" class="spinner-border spinner-border-sm me-1" role="status"></span>
							<font-awesome-icon v-else :icon="['fas', 'check']" class="me-1" />{{ updatingClient ? 'Updating…' : 'Update' }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import { Modal, Tooltip } from 'bootstrap'
import moment from 'moment-timezone'
import { utcToLocal, fromNowUtc, formatUtc } from '@/utils/time'
import { buildDashboardLoginUrl } from '@/utils/dashboardLoginUrl'
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import restApi from '../../../api/index'
import MediaLibraryModal from '@/components/juniper/media/MediaLibraryModal'
import JuniperClientUpgradeCard from '@/components/juniper/clients/JuniperClientUpgradeCard'
import ReportDetailsModal from '@/components/juniper/ada-auditor/ReportDetailsModal.vue'
import juniperUserMixin from '@/mixins/juniperUserMixin'

// Small inline component that animates a number from its previous value up to
// the new target (easeOutCubic) whenever the bound value changes.
const CountUp = {
	name: 'CountUp',
	props: {
		value: { type: Number, default: 0 },
		duration: { type: Number, default: 900 },
	},
	data() {
		return { display: 0 }
	},
	mounted() {
		this.animate(0, this.value)
	},
	watch: {
		value(n) {
			this.animate(this.display, n)
		},
	},
	methods: {
		animate(from, to) {
			cancelAnimationFrame(this._raf)
			const start = performance.now()
			const diff = to - from
			const step = now => {
				const t = Math.min(1, (now - start) / this.duration)
				const eased = 1 - Math.pow(1 - t, 3)
				this.display = Math.round(from + diff * eased)
				if (t < 1) this._raf = requestAnimationFrame(step)
				else this.display = to
			}
			this._raf = requestAnimationFrame(step)
		},
	},
	beforeDestroy() {
		cancelAnimationFrame(this._raf)
	},
	render(h) {
		return h('span', this.display)
	},
}

export default {
	name: 'EditClient',
	data() {
		return {
			zoom: 12,
			center: { lat: 44.8228, lng: -87.366 },
			loading: false,
			lodingContantStats: false,
			dashboardUrl: process.env.VUE_APP_DASHBOARD_URL,
			client: {},
			marker: {},
			mcClient: null,
			upgradeStatus: null,
			csKey: 1,
			exportData: {
				customizer: false,
				pages: false,
				news: false,
				posts: false,
				departments: false,
				faqs: false,
				events: false,
				meetings: false,
				subscribers: false,
			},
			launchModal: null,
			metaModal: null,
			originalUrl: null,
			renamePleskSubscription: false,
			updatingClient: false,
			exportModal: null,
			mediaModal: null,
			deleteModal: null,
			migrateStagingModal: null,
			deleteLoading: false,
			deleteConfirmText: '',
			launchLoading: false,
			migratingStagingUrl: false,
			totalPages: 0,
			totalPagesStagingUrl: 0,
			pagesReplacingLoading: false,
			pagesReplacingDone: false,
			totalNews: 0,
			totalNewsStagingUrl: 0,
			newsReplacingLoading: false,
			newsReplacingDone: false,
			totalDepartments: 0,
			totalDepartmentsStagingUrl: 0,
			departmentsReplacingLoading: false,
			departmentsReplacingDone: false,
			contentGeneratingLoading: false,
			contentGeneratingDone: false,
			totalEvents: 0,
			totalMeetings: 0,
			totalLandingPages: 0,
			totalBusinessDirectory: 0,
			totalFiles: 0,
			totalSubscribers: 0,
			clientOptions: null,
			errorAlerts: [],
			warningAlerts: [],
			showErrorAlerts: true,
			showWarningAlerts: true,
			errorAlertsDismissed: false,
			warningAlertsDismissed: false,
			errorAlertTimer: null,
			warningAlertTimer: null,
			lastFullStatic: null,
			// Latest finished ADA audit report (badges next to the client name)
			adaReport: null,
			adaModalOpen: false,
			// Juniper Settings → ADA Compliance → Launch blockers (mc2-api defaults)
			adaLaunchBlockers: { unified_only: true, no_audit: true, theme: true, content: true, document: false },
			loadingLastStatic: false,
			featureTooltips: [],
			// Newest activity-log entry for this client — drives the "last edited"
			// chip in the hero (who touched the client last, what and when).
			lastActivity: null,
			itsFriday: new Date().getDay() === 5,
		}
	},
	components: { MediaLibraryModal, JuniperClientUpgradeCard, CountUp, ReportDetailsModal },
	computed: {
		...mapState(['authUser', 'tokenJuniper', 'projectManagers']),
		...mapGetters(['isAuth', 'projectManagerById']),
		live() {
			return !this.client.staging
		},
		// Latest finished ADA audit's errors per source; blocking = enabled in
		// Juniper Settings → ADA Compliance → Launch blockers and count > 0
		adaLaunchBuckets() {
			const bySource = (this.adaReport && this.adaReport.errors_by_source) || {}
			return [
				{ key: 'theme', label: 'Theme' },
				{ key: 'content', label: 'content' },
				{ key: 'document', label: 'files' },
			].map(b => {
				const count = bySource[b.key] || 0
				return { ...b, count, blocking: !!this.adaLaunchBlockers[b.key] && count > 0 }
			})
		},
		// "Unified Dashboard clients only" → Old (Town Web) clients skip the ADA check
		adaLaunchApplies() {
			return !this.adaLaunchBlockers.unified_only || this.client.dashboard === 'unified'
		},
		adaLaunchBlockReason() {
			if (!this.adaLaunchApplies) return null
			if (!this.adaReport || !this.adaReport.errors_by_source) {
				return this.adaLaunchBlockers.no_audit ? 'ADA audit required before launch' : null
			}
			const blocking = this.adaLaunchBuckets.filter(b => b.blocking).map(b => b.label.toLowerCase())
			return blocking.length ? `Fix ADA ${blocking.join('/')} errors before launch` : null
		},
		adaLaunchBlocked() {
			return !!this.adaLaunchBlockReason
		},
		// Live mc2 name wins so a rename shows immediately; the snapshot stored on
		// the client covers anyone credited who isn't on the current whitelist.
		projectManagerName() {
			if (!this.client.project_manager_id) return ''
			return this.projectManagerById[this.client.project_manager_id]?.name || this.client.project_manager_name || ''
		},
		// Credited to someone the whitelist no longer offers (an older assignment,
		// or a name backfilled by hand).
		projectManagerUnlisted() {
			return !!this.client.project_manager_id && !this.projectManagerById[this.client.project_manager_id]
		},
		lastActivityTooltip() {
			const activity = this.lastActivity
			if (!activity) return ''
			const who = activity.causer ? activity.causer.name : 'System'
			const subject = activity.subject_type ? activity.subject_type.split('\\').pop() : ''
			const what = [activity.event, subject && subject + (activity.subject_id ? ' #' + activity.subject_id : '')]
				.filter(Boolean)
				.join(' ')
			return `<strong>${who}</strong> ${what}<br>${fromNowUtc(activity.created_at)}`
		},
		urlChanged() {
			const normalize = u =>
				(u || '')
					.trim()
					.toLowerCase()
					.replace(/^https?:\/\//, '')
					.replace(/\/+$/, '')
			return !!normalize(this.originalUrl) && !!normalize(this.client.url) && normalize(this.originalUrl) !== normalize(this.client.url)
		},
		isUpgradeClient() {
			return !!(this.upgradeStatus && this.upgradeStatus.isUpgrade)
		},
		upgradeUrl() {
			// Visible only when on the LIVE client page that has an upgrade
			if (!this.upgradeStatus || this.upgradeStatus.isUpgrade) return null
			if (!this.upgradeStatus.hasUpgrade || !this.upgradeStatus.upgrade) return null
			return this.upgradeStatus.upgrade.staging_url || this.upgradeStatus.upgrade.url || null
		},
		upgradeDashboardUrl() {
			if (!this.upgradeUrl) return null
			// Built from the copy's row: it decides unified vs town-web, and the
			// unified link carries ?juniper=upgrade so HeyGov edits the copy.
			return buildDashboardLoginUrl(this.upgradeStatus.upgrade, {
				dashboardUrl: this.dashboardUrl,
				email: this.authUser.email,
				tokenJuniper: this.tokenJuniper,
				target: 'upgrade',
				appUrl: this.clientOptions.heygov_app_url,
			})
		},
		liveUrl() {
			// Visible only when on the UPGRADE page — points to live client URL
			if (!this.upgradeStatus || !this.upgradeStatus.isUpgrade) return null
			const live = this.upgradeStatus.liveClient
			if (!live) return null
			return live.staging ? live.staging_url : (live.url || live.staging_url) || null
		},
		clientSettingsRoute() {
			if (!this.client || !this.client.id) return '/juniper/clients'
			return '/juniper/clients/edit/' + this.client.id + '/settings'
		},
		isDeleteUpgrade() {
			return this.client && this.client.status === 'upgrade'
		},
		deleteUrl() {
			if (!this.client) return null
			return this.client.staging ? this.client.staging_url : (this.client.url || this.client.staging_url)
		},
		canConfirmDelete() {
			if (!this.client || !this.client.name) return false
			return this.deleteConfirmText.trim() === this.client.name.trim()
		},
		// True for legacy clients whose staging URL is still on the old townweb.com
		// zone. Shows a "Migrate" badge next to the staging URL link.
		canMigrateStagingUrl() {
			const url = (this.client && this.client.staging_url) || ''
			return url.includes('.townweb.com')
		},
		// Preview of the URL that the backend will generate after migration.
		// Mirrors juniper-api's Client::buildStagingUrl helper.
		newStagingUrlPreview() {
			if (!this.client || !this.client.id) return '—'
			const padded = String(this.client.id).padStart(4, '0')
			return `https://jp${padded}stg.townweb.site`
		},
		// MC2 feature badges (WGS, Facebook syndication, HeyGov modules) derived
		// from the linked MC2 client. Shown as small icon chips before the theme
		// badge. Flags are 'yes'/'no' strings on the MC2 client model.
		mcFeatures() {
			const c = this.mcClient
			if (!c) return []
			const isYes = v => v === 'yes' || v === true || v === 1 || v === '1'
			const wgsActive = !!c.white_glove_service && c.white_glove_service !== 'no' && c.white_glove_service !== '0'
			const f = []
			if (wgsActive) {
				const wgsVal = String(c.white_glove_service).trim()
				let wgsValLabel = wgsVal
				// For a trial (e.g. "Free trial"), also show the expiration date.
				if (wgsVal.toLowerCase().includes('trial') && c.white_glove_service_expiration) {
					wgsValLabel = wgsVal + ' until ' + moment(c.white_glove_service_expiration).format('MMM D, YYYY')
				}
				f.push({
					key: 'wgs',
					label: 'White Glove Service',
					tip: 'White Glove Service (' + wgsValLabel + ') — premium, hands-on managed support for this client',
					icon: ['fas', 'mitten'],
					cls: 'ce-feat--wgs',
				})
			}
			const fbVal = String(c.facebook_syndication || '').toLowerCase()
			if (fbVal.indexOf('yes') === 0) {
				const fbStatus = fbVal.indexOf('not') !== -1 ? 'enabled (not yet implemented)' : 'implemented'
				f.push({ key: 'fb', label: 'Facebook Syndication', tip: 'Facebook Syndication — ' + fbStatus + '; news &amp; notices are automatically posted to the client\'s Facebook page', icon: ['fab', 'facebook'], cls: 'ce-feat--fb' })
			}
			if (isYes(c.heygov_311)) f.push({ key: 'hg311', label: 'HeyGov 311', tip: 'HeyGov 311 — citizen requests &amp; issue reporting', icon: ['fas', 'bullhorn'], cls: 'ce-feat--hg' })
			if (isYes(c.heygov_pay)) f.push({ key: 'hgpay', label: 'HeyGov Pay', tip: 'HeyGov Pay — online payments (utilities, fees, etc.)', icon: ['fas', 'credit-card'], cls: 'ce-feat--hg' })
			if (isYes(c.heygov_reserve)) f.push({ key: 'hgres', label: 'HeyGov Reserve', tip: 'HeyGov Reserve — facility &amp; venue reservations / bookings', icon: ['fas', 'calendar-check'], cls: 'ce-feat--hg' })
			if (isYes(c.heygov_license)) f.push({ key: 'hglic', label: 'HeyGov Licensing', tip: 'HeyGov Licensing — permits &amp; licensing management', icon: ['fas', 'file-contract'], cls: 'ce-feat--hg' })
			if (isYes(c.heygov_clerk_minutes)) f.push({ key: 'hgmin', label: 'HeyGov Clerk Minutes', tip: 'HeyGov Clerk Minutes — meeting agendas &amp; minutes management', icon: ['fas', 'gavel'], cls: 'ce-feat--hg' })
			return f
		},
		// The map status key drives the recoloured geometry palette below
		// (mirrors the status pill): live → green, staging → blue, etc.
		mapStatusKey() {
			if (!this.client) return 'default'
			if (this.client.maintenance_mode) return 'maintenance'
			if (!this.client.staging) return 'live'
			if (this.client.status === 'upgrade') return 'upgrade'
			if (this.client.staging) return 'staging'
			return 'default'
		},
		// Non-interactive hero map whose geometry is recoloured per status — the
		// landmass/water/roads themselves turn green (live) or blue (staging),
		// instead of laying a coloured overlay on top of a neutral map.
		mapOptions() {
			const palettes = {
				live:        { land: '#e3f3e8', water: '#a9d6b8', road: '#ffffff', road2: '#cdebd6', hwy: '#bfe3cb', stroke: '#b6dcc1', text: '#4f7c61' },
				staging:     { land: '#e6edfa', water: '#aac7ef', road: '#ffffff', road2: '#d2e0f6', hwy: '#c4d6f1', stroke: '#bccdeb', text: '#566f97' },
				upgrade:     { land: '#efe9fb', water: '#c7b6ef', road: '#ffffff', road2: '#e0d4f6', hwy: '#d4c2f1', stroke: '#cdbceb', text: '#6b5897' },
				maintenance: { land: '#fbf1df', water: '#eecf9a', road: '#ffffff', road2: '#f5e6cd', hwy: '#f1dcbb', stroke: '#ebd9bc', text: '#977f56' },
				default:     { land: '#f4f6f8', water: '#cfe0ec', road: '#ffffff', road2: '#eef1f4', hwy: '#e3e8ee', stroke: '#d7dde3', text: '#9aa3ad' },
			}
			const p = palettes[this.mapStatusKey] || palettes.default
			return {
				disableDefaultUI: true,
				gestureHandling: 'none',
				keyboardShortcuts: false,
				clickableIcons: false,
				styles: [
					{ elementType: 'geometry', stylers: [{ color: p.land }] },
					{ elementType: 'labels.text.fill', stylers: [{ color: p.text }] },
					{ elementType: 'labels.text.stroke', stylers: [{ color: p.land }] },
					{ featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: p.stroke }] },
					{ featureType: 'landscape', elementType: 'geometry', stylers: [{ color: p.land }] },
					{ featureType: 'poi', stylers: [{ visibility: 'off' }] },
					{ featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: p.road2 }] },
					{ featureType: 'road', elementType: 'geometry', stylers: [{ color: p.road }] },
					{ featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: p.road2 }] },
					{ featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: p.hwy }] },
					{ featureType: 'transit', stylers: [{ visibility: 'off' }] },
					{ featureType: 'water', elementType: 'geometry', stylers: [{ color: p.water }] },
				],
			}
		},
		// Relative time of the last successful full-static build, pulled from the
		// deployments (static_generations) table — type=full-static, status=success.
		lastStaticRelative() {
			// static_generations only keeps runs that produced changes (a
			// no-change regen deletes its row), so also consider the client's
			// last_static_generated_at — it is bumped on every full regen —
			// and show whichever is newer. Timestamps arrive as naive UTC
			// strings, so parse via utcToLocal or they show hours off.
			const genTs = this.lastFullStatic
				? this.lastFullStatic.finished_at || this.lastFullStatic.started_at
				: null
			const clientTs = this.client ? this.client.last_static_generated_at : null
			let ts = genTs
			if (clientTs && (!ts || utcToLocal(clientTs).isAfter(utcToLocal(ts)))) ts = clientTs
			return ts ? fromNowUtc(ts) : '—'
		},
	},

	mounted() {
		this.launchModal = new Modal(this.$refs.launchModal)
		this.metaModal = new Modal(this.$refs.metaModal)
		this.exportModal = new Modal(this.$refs.exportModal)
		this.mediaModal = new Modal('#mediaLibraryModal')
		this.deleteModal = new Modal(this.$refs.deleteModal)
		this.migrateStagingModal = new Modal(this.$refs.migrateStagingModal)
		this.$refs.deleteModal.addEventListener('hidden.bs.modal', () => {
			this.deleteConfirmText = ''
		})
		// Header quick-actions strip finished a full-static run — refresh the
		// "Last static" label without a page reload.
		this.$root.$on('juniper-static-generated', this.onStaticGenerated)
	},

	created() {
		this.getClient()
		this.loadAdaReport()
		this.loadAdaLaunchBlockers()
		this.loadLastActivity()
		this.$store.dispatch('fetchProjectManagers')
	},
	beforeDestroy() {
		clearTimeout(this.errorAlertTimer)
		clearTimeout(this.warningAlertTimer)
		this.disposeFeatureTooltips()
		this.$root.$off('juniper-static-generated', this.onStaticGenerated)
	},
	methods: {
		// Initialise Bootstrap tooltips on the MC2 feature chips (re-run when
		// the feature list changes, since the chips are re-rendered).
		initFeatureTooltips() {
			this.$nextTick(() => {
				this.disposeFeatureTooltips()
				const els = this.$el.querySelectorAll('.ce-feat[data-bs-toggle="tooltip"], .ce-dash-badge[data-bs-toggle="tooltip"], .ce-ada-badge[data-bs-toggle="tooltip"], .ce-pm-badge[data-bs-toggle="tooltip"]')
				this.featureTooltips = Array.from(els).map(el => new Tooltip(el))
			})
		},

		// Newest entry from the client activity log (same endpoint as the
		// Activities page) — feeds the "last edited" hero chip.
		loadLastActivity() {
			juniperApi
				.get('/activities/client/' + this.$route.params.id, { params: { page: 1 } })
				.then(({ data }) => {
					const items = (data && data.data) || []
					this.lastActivity = items[0] || null
					if (this.lastActivity) this.initFeatureTooltips()
				})
				.catch(() => {
					this.lastActivity = null
				})
		},

		// Latest finished ADA report summary — same source as the clients-table
		// badges (poor_pages / warn_pages / total_pages from mc2-api).
		loadAdaReport() {
			restApi
				.get(`/juniper/ada-auditor/clients/${this.$route.params.id}/audit-reports`, { params: { limit: 5 } })
				.then(({ data }) => {
					const items = (data && data.items) || []
					this.adaReport = items.find(r => r.status === 'finished') || null
					if (this.adaReport) this.initFeatureTooltips()
				})
				.catch(error => {
					console.error('ADA report load failed', error)
				})
		},

		loadAdaLaunchBlockers() {
			restApi
				.get('/juniper/ada-auditor/settings')
				.then(({ data }) => {
					if (data && data.launch_blockers) this.adaLaunchBlockers = data.launch_blockers
				})
				.catch(error => {
					console.error('ADA launch blockers load failed', error)
				})
		},

		adaOkTooltip(report) {
			const total = report.total_pages || 0
			return `<strong>${total}/${total}</strong> pages error-free<br>${this.adaAuditedLine(report)}`
		},

		adaErrorTooltip(report) {
			const total = report.total_pages || 0
			return (
				`<strong>${report.poor_pages}</strong> of <strong>${total}</strong> pages have errors` +
				(report.errors ? ` (${report.errors} errors total)` : '') +
				`<br>${this.adaAuditedLine(report)}`
			)
		},

		adaWarnTooltip(report) {
			const total = report.total_pages || 0
			return (
				`<strong>${report.warn_pages}</strong> of <strong>${total}</strong> pages have warnings` +
				(report.warnings ? ` (${report.warnings} warnings total)` : '') +
				`<br>${this.adaAuditedLine(report)}`
			)
		},

		adaAuditedLine(report) {
			const at = report.finished_at || report.created_at
			if (!at) return 'Audit date unknown'
			return `Audited ${fromNowUtc(at)} (${formatUtc(at, 'MMM D, YYYY [at] h:mm A')})`
		},
		disposeFeatureTooltips() {
			if (this.featureTooltips) {
				this.featureTooltips.forEach(t => {
					try { t.dispose() } catch (e) { /* already gone */ }
				})
			}
			this.featureTooltips = []
		},
		onUpgradeChanged() {
			this.getClient()
		},
		onUpgradeStatus(status) {
			this.upgradeStatus = status
		},
		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.originalUrl = data.url
					if (this.client.type === 'business') {
						this.$router.push({
							name: 'juniperClientEditBusiness',
							params: { id: this.client.id },
						})
					}
					this.getLastFullStatic()
					this.getClientContentStats()
					this.getClientOptions()
					// NOTE: loading is cleared by getClientOptions() so the skeleton
					// stays visible until clientOptions arrive (template is gated on
					// !loading && client && clientOptions — clearing loading here
					// would leave a blank page between the two API responses).
					if (this.client.mc_client_id !== null) {
						this.getClientFromMissionControl()
					}
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		getClientContentStats() {
			this.lodingContantStats = true
			juniperApi
				.get('/clients/' + this.$route.params.id + '/contentStats')
				.then(({ data }) => {
					this.totalPages = data['totalPages']
					this.totalPagesStagingUrl = data['totalPagesStagingUrl']
					this.totalNews = data['totalNews']
					this.totalNewsStagignUrl = data['totalNewsStagingUrl']
					this.totalDepartments = data['totalDepartments']
					this.totalDepartmentsStagingUrl = data['totalDepartmentsStagingUrl']
					this.totalEvents = data['totalEvents']
					this.totalMeetings = data['totalMeetings']
					this.totalLandingPages = data['totalLandingPages']
					this.totalBusinessDirectory = data['totalBusinessDirectory']
					this.totalFiles = data['totalFiles']
					this.totalSubscribers = data['totalSubscribers']
					this.lodingContantStats = false
				})
				.catch(error => {
					this.lodingContantStats = false
					console.log(error)
				})
		},

		// Toggle maintenance mode
		maintenanceModeSwitch() {
			if (this.client.maintenance_mode) {
				this.$swal
					.fire({
						title: 'Are you sure?',
						text: 'The website will be put into maintenance mode and will not be operational',
						icon: 'warning',
						position: 'center',
						showCancelButton: true,
						showConfirmButton: true,
						confirmButtonColor: '#3085d6',
						cancelButtonColor: '#d33',
						confirmButtonText: 'Yes, put in maintenance mod!',
						toast: false,
						timer: 0,
					})
					.then(result => {
						if (result.isConfirmed) {
							restApi
								.post('/juniper/client/maintenance', {
									mode: 'on',
									id: this.client.id,
									ftp_user: this.client.ftp_user,
									ftp_password: this.client.ftp_password,
								})
								.then(() => {
									this.maintenanceModeUpdate('on')
								})
								.catch(error => {
									this.loading = false
									console.log(error)
								})
						} else {
							this.client.maintenance_mode = false
						}
					})
			} else {
				this.$swal
					.fire({
						title: 'Are you sure?',
						text: 'The website maintenance mode will be off',
						icon: 'warning',
						position: 'center',
						showCancelButton: true,
						showConfirmButton: true,
						confirmButtonColor: '#3085d6',
						cancelButtonColor: '#d33',
						confirmButtonText: 'Yes, turn maintenance mode off!',
						toast: false,
						timer: 0,
					})
					.then(result => {
						if (result.isConfirmed) {
							restApi
								.post('/juniper/client/maintenance', {
									mode: 'off',
									id: this.client.id,
									ftp_user: this.client.ftp_user,
									ftp_password: this.client.ftp_password,
								})
								.then(() => {
									this.maintenanceModeUpdate('off')
								})
								.catch(error => {
									this.loading = false
									console.log(error)
								})
						} else {
							this.client.maintenance_mode = true
						}
					})
			}
		},

		// Update maintenance mode in Juniper CMS Database
		maintenanceModeUpdate(mode) {
			juniperApi
				.post(`/clients/${this.client.id}/maintenance`, { mode: mode })
				.then(() => {
					this.$swal.fire({
						icon: 'success',
						title: `Maintenance mode '${mode}'`,
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: `Error with updating maintenance mode: ${error}`,
						timer: 6000,
					})
				})
		},

		// Remove client
		deleteClient() {
			this.deleteModal.show()
		},

		async confirmDelete() {
			this.deleteLoading = true
			try {
				if (this.client.status === 'upgrade') {
					await juniperApi.delete(`/clients/${this.client.id}/upgrade`)
				} else {
					await juniperApi.delete(`/clients/${this.client.id}/force`)
				}
				this.deleteModal.hide()
				this.$router.push('/juniper/clients')
			} catch (err) {
				this.deleteModal.hide()
				this.$swal.fire({
					icon: 'error',
					title: 'Delete failed.',
					text: err?.response?.data?.message ?? err.message,
				})
			} finally {
				this.deleteLoading = false
			}
		},

		// Migrate legacy townweb.com staging URL → jp{id}stg.townweb.site.
		// Opens the confirmation modal; the actual work is done in confirmMigrateStagingUrl.
		migrateStagingUrl() {
			if (this.migrateStagingModal) this.migrateStagingModal.show()
		},

		// Performs the migration: adds a new Plesk alias + Cloudflare DNS and
		// updates the stored staging_url. The old townweb.com alias stays in place.
		async confirmMigrateStagingUrl() {
			this.migratingStagingUrl = true
			try {
				const { data } = await juniperApi.post(`/clients/${this.client.id}/staging-url/migrate`)
				this.client.staging_url = data.staging_url
				if (this.migrateStagingModal) this.migrateStagingModal.hide()
				this.$swal.fire({
					icon: 'success',
					title: 'Staging URL migrated',
					html: `New staging URL: <code>${data.staging_url}</code>`,
					timer: 6000,
				})
			} catch (err) {
				const msg = err?.response?.data?.error ?? err?.response?.data?.message ?? err.message
				this.$swal.fire({
					icon: 'error',
					title: 'Migration failed',
					text: msg,
				})
			} finally {
				this.migratingStagingUrl = false
			}
		},

		// Make website live
		async launchWebsite() {
			this.launchLoading = true
			await this.pagesReplacingStagingUrl()
			await this.newsReplacingStagingUrl()
			await this.departmentsReplacingStagingUrl()
			await this.generateStatic()
			juniperApi
				.post(`/clients/${this.$route.params.id}/launch`)
				.then(() => {
					setTimeout(() => {
						this.launchModal.hide()
						this.launchLoading = false
						this.getClient()
						this.$swal.fire({
							icon: 'success',
							title: `Website is now Live!!!`,
							timet: 6000,
						})
					}, '2000')
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: `Error with updating maintenance mode: ${error}`,
						timer: 6000,
					})
				})
		},

		async pagesReplacingStagingUrl() {
			this.pagesReplacingLoading = true
			await juniperApi
				.get('/clients/' + this.$route.params.id + '/stagingUrlInPages')
				.then(() => {
					this.pagesReplacingLoading = false
					this.pagesReplacingDone = true
				})
				.catch(error => {
					this.pagesReplacingLoading = false
					console.log(error)
				})
		},

		async newsReplacingStagingUrl() {
			this.newsReplacingLoading = true
			await juniperApi
				.get('/clients/' + this.$route.params.id + '/stagingUrlInNewsNotices')
				.then(() => {
					this.newsReplacingLoading = false
					this.newsReplacingDone = true
				})
				.catch(error => {
					this.newsReplacingLoading = false
					console.log(error)
				})
		},

		async departmentsReplacingStagingUrl() {
			this.departmentsReplacingLoading = true
			await juniperApi
				.get('/clients/' + this.$route.params.id + '/stagingUrlInDepartments')
				.then(() => {
					this.departmentsReplacingLoading = false
					this.departmentsReplacingDone = true
				})
				.catch(error => {
					this.departmentsReplacingLoading = false
					console.log(error)
				})
		},

		// Generate static content
		async generateStatic() {
			this.contentGeneratingLoading = true
			await juniperApi
				.post('/clients/' + this.$route.params.id + '/static/generate')
				.then(() => {
					this.contentGeneratingLoading = false
					this.contentGeneratingDone = true
					this.$swal.fire({
						toast: true,
						position: 'top-end',
						icon: 'success',
						title: 'Static generation finished',
						showConfirmButton: false,
						timer: 3000,
						timerProgressBar: true,
					})
				})
				.catch(error => {
					this.contentGeneratingLoading = false
					this.$swal.fire({
						toast: true,
						position: 'top-end',
						icon: 'error',
						title: error.response?.data?.message || 'Static generation failed',
						showConfirmButton: false,
						timer: 5000,
						timerProgressBar: true,
					})
					console.log(error)
				})
		},

		insertPhoto(photoUrl) {
			this.client.logo = photoUrl
			this.updateLogo()
			this.mediaModal.hide()
		},

		showMediaModal(e) {
			this.mediaElement = e
			this.mediaModal.show()
		},

		hideMediaModal() {
			this.mediaModal.hide()
		},

		updateLogo() {
			juniperApi
				.put('/clients/update/' + this.$route.params.id + '/logo', this.client)
				.then(({ data }) => {
					this.client = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		removeLogo() {
			this.client.logo = null
			this.updateLogo()
		},

		getDashboardLoginUrl() {
			return buildDashboardLoginUrl(this.client, {
				dashboardUrl: this.dashboardUrl,
				email: this.authUser.email,
				tokenJuniper: this.tokenJuniper,
				appUrl: this.clientOptions.heygov_app_url,
			})
		},

		// The name is stored alongside the id as a snapshot, so use $set — on a
		// client that has never had a PM both keys come back null from the API,
		// but a freshly created one may not carry them at all.
		setProjectManager(value) {
			const id = value ? Number(value) : null
			this.$set(this.client, 'project_manager_id', id)
			this.$set(
				this.client,
				'project_manager_name',
				id ? this.projectManagerById[id]?.name || this.client.project_manager_name || null : null
			)
		},

		updateClient() {
			this.updatingClient = true
			const payload = {
				...this.client,
				rename_plesk_subscription: this.renamePleskSubscription && this.urlChanged,
			}
			juniperApi
				.put('/clients/' + this.$route.params.id, payload)
				.then(({ data }) => {
					this.originalUrl = (data && data.url) || this.client.url
					this.renamePleskSubscription = false
					if (this.metaModal) this.metaModal.hide()
					this.$swal.fire({
						icon: 'success',
						title: 'Client updated successfully',
						timer: 2000,
					})
				})
				.catch(error => {
					this.loading = false
					console.log(error)
					const msg = (error.response && error.response.data && error.response.data.error) || 'Client update failed'
					this.$swal.fire({
						icon: 'error',
						title: 'Update failed',
						text: msg,
					})
				})
				.finally(() => {
					this.updatingClient = false
				})
		},

		inputLocation(place) {
			this.center = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() }
		},

		setClient(client) {
			if (client) {
				this.client.mc_client_id = client.id
				this.center = { lat: parseFloat(client.latitude), lng: parseFloat(client.longitude) }
				this.updateClient()
			}
		},

		getClientFromMissionControl() {
			// NOTE: do NOT touch `loading` here. This is a secondary fetch and
			// the skeleton must stay tied to client + clientOptions only —
			// otherwise it disappears whenever this (fast) call resolves first.
			//
			// IMPORTANT: this MC2 lookup is non-critical. We deliberately use a
			// bare axios call (NOT the shared restApi instance) so a 401 here can
			// never trip the global "force logout + reload" interceptor. If the
			// MC2 session is missing/expired we simply skip the MC2 extras.
			const baseUrl = this.$store.state.apiUrl
			const headers = {}
			if (this.$store.state.token) {
				headers['Authorization'] = `Bearer ${this.$store.state.token}`
			}
			axios
				.get(baseUrl + '/clients/' + this.client.mc_client_id, { headers })
				.then(({ data }) => {
					this.mcClient = data
					if (data.municipality) {
						this.center = { lat: parseFloat(this.mcClient.municipality.lat), lng: parseFloat(this.mcClient.municipality.lng) }
					} else {
						this.center = { lat: parseFloat(this.mcClient.latitude), lng: parseFloat(this.mcClient.longitude) }
					}
				})
				.catch(error => {
					console.error('Failed to load MC2 client:', error)
					// MC2 client doesn't exist or session expired — this is OK,
					// just skip the MC2 extras without disturbing the session.
					this.mcClient = null
				})
		},

		getClientOptions() {
			// loading is set true by getClient(); leave it set so the skeleton
			// stays visible across both /clients/:id and /options/:id calls.
			this.loading = true
			juniperApi
				.post('/options/' + this.$route.params.id)
				.then(({ data }) => {
					this.clientOptions = data
					if (this.clientOptions.subscribable_terms) {
						if (this.clientOptions.subscribable_terms['App\\Models\\MeetingRepositoryCategory']) {
							this.meetingRepositoryCategoriesSubscribable = this.clientOptions.subscribable_terms['App\\Models\\MeetingRepositoryCategory']
						}
						if (this.clientOptions.subscribable_terms['App\\Models\\NewsNoticeCategory']) {
							this.newsNoticeCategoriesSubscribable = this.clientOptions.subscribable_terms['App\\Models\\NewsNoticeCategory']
						}
					}
					if (this.clientOptions.meeting_table_active_columns) {
						this.meetingTableColumnsActive = this.clientOptions.meeting_table_active_columns
					}
					this.loading = false
					// Hero chips render only after clientOptions arrive — (re)bind
					// their tooltips now that the elements exist.
					this.initFeatureTooltips()
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		checkForWarnings() {
			this.warningAlerts = []
			if (this.isBlank(this.clientOptions.google_analytic_code)) {
				this.warningAlerts.push({
					id: 'google_analytics_code',
					title: 'Google Analytics code is missing.',
					description: 'Add a tracking code in Settings so traffic and behavior analytics can be collected.',
				})
			}
			if (this.warningAlerts.length === 0) this.warningAlertsDismissed = false
			this.showWarningAlerts = this.warningAlerts.length > 0 && !this.warningAlertsDismissed
			this.scheduleAlertDismiss('warning')
		},

		// Auto-hide a setup-alert toast 15s after it appears.
		scheduleAlertDismiss(kind) {
			if (kind === 'error') {
				clearTimeout(this.errorAlertTimer)
				if (this.showErrorAlerts) {
					this.errorAlertTimer = setTimeout(() => this.dismissErrorAlerts(), 15000)
				}
			} else {
				clearTimeout(this.warningAlertTimer)
				if (this.showWarningAlerts) {
					this.warningAlertTimer = setTimeout(() => this.dismissWarningAlerts(), 15000)
				}
			}
		},

		checkForErrors() {
			this.errorAlerts = []
			if (this.isBlank(this.clientOptions.heygov_api_url)) {
				this.errorAlerts.push({
					id: 'heygov_api_url',
					title: 'HeyGov API URL is missing.',
					description: 'Set the API endpoint in Settings to enable HeyGov integration and request synchronization.',
				})
			}
			if (this.isBlank(this.clientOptions.heygov_app_url)) {
				this.errorAlerts.push({
					id: 'heygov_app_url',
					title: 'HeyGov APP URL is missing.',
					description: 'Configure the app URL in Settings so internal links and redirects point to the correct HeyGov app.',
				})
			}
			if (this.isBlank(this.clientOptions.heygov_jurisdiction)) {
				this.errorAlerts.push({
					id: 'heygov_jurisdiction',
					title: 'HeyGov jurisdiction is missing.',
					description: 'Set the jurisdiction value in Settings to map requests to the correct municipality.',
				})
			}
			if (this.isBlank(this.clientOptions.heygov_api_key)) {
				this.errorAlerts.push({
					id: 'heygov_api_key',
					title: 'HeyGov API key is missing.',
					description: 'Add a valid API key in Settings to authenticate requests to HeyGov services.',
				})
			}
			if (this.isBlank(this.clientOptions.mailer)) {
				this.errorAlerts.push({
					id: 'mailer_provider',
					title: 'Mailer provider is not selected.',
					description: 'Choose a mailer provider in Settings so system emails can be delivered.',
				})
			} else {
				if (this.clientOptions.mailer && this.clientOptions.mailer == 'sendgrid') {
					if (this.isBlank(this.clientOptions.mailer_sendgrid_key)) {
						this.errorAlerts.push({
							id: 'sendgrid_key',
							title: 'SendGrid API key is missing.',
							description: 'Add the SendGrid key in Settings so outbound emails can be sent.',
						})
					}
				}
				if (this.clientOptions.mailer && this.clientOptions.mailer == 'mailgun') {
					if (this.isBlank(this.clientOptions.mailer_mailgun_key)) {
						this.errorAlerts.push({
							id: 'mailgun_key',
							title: 'Mailgun API key is missing.',
							description: 'Add the Mailgun key in Settings so email delivery can be authorized.',
						})
					}
					if (this.isBlank(this.clientOptions.mailer_mailgun_domain)) {
						this.errorAlerts.push({
							id: 'mailgun_domain',
							title: 'Mailgun domain is missing.',
							description: 'Set the Mailgun sending domain in Settings to route emails through the correct domain.',
						})
					}
				}
			}
			if (this.errorAlerts.length === 0) this.errorAlertsDismissed = false
			this.showErrorAlerts = this.errorAlerts.length > 0 && !this.errorAlertsDismissed
			this.scheduleAlertDismiss('error')
		},
		dismissErrorAlerts() {
			clearTimeout(this.errorAlertTimer)
			this.errorAlertsDismissed = true
			this.showErrorAlerts = false
		},
		dismissWarningAlerts() {
			clearTimeout(this.warningAlertTimer)
			this.warningAlertsDismissed = true
			this.showWarningAlerts = false
		},
		isBlank(value) {
			return value === null || value === undefined || String(value).trim() === ''
		},

		// The header quick-actions strip finished a full-static run for some
		// client — if it's this one, bump the timestamp and re-fetch the
		// latest deployment row.
		onStaticGenerated(clientId) {
			if (String(clientId) !== String(this.$route.params.id)) return
			if (this.client) this.client.last_static_generated_at = new Date().toISOString()
			this.getLastFullStatic()
		},

		// Fetch the most recent successful full-static build from the
		// deployments (static_generations) table. Used for the "Last static" label.
		getLastFullStatic() {
			this.loadingLastStatic = true
			juniperApi
				.get('/clients/' + this.$route.params.id + '/static/generations', {
					params: { type: 'full-static', status: 'success', per_page: 1 },
				})
				.then(({ data }) => {
					const rows = data.data || data || []
					this.lastFullStatic = rows.length ? rows[0] : null
					this.loadingLastStatic = false
				})
				.catch(error => {
					this.loadingLastStatic = false
					console.log(error)
				})
		},

		formatDate(date) {
			if (!date) return ''
			const d = new Date(date)
			const year = d.getFullYear()
			const month = String(d.getMonth() + 1).padStart(2, '0')
			const day = String(d.getDate()).padStart(2, '0')
			return `${year}-${month}-${day}`
		},

		stripDeleteProtocol(url) {
			if (!url) return ''
			return url.replace(/^https?:\/\//, '')
		},

		ensureDeleteUrl(url) {
			if (!url) return '#'
			return /^https?:\/\//.test(url) ? url : `https://${url}`
		},
	},
	watch: {
		clientOptions: function() {
			this.checkForErrors()
			this.checkForWarnings()
		},
		// (Re)initialise Bootstrap tooltips whenever the MC2 feature chips change.
		mcFeatures: function() {
			this.initFeatureTooltips()
		},
		// The feature chips live behind v-if="!loading ...". When the skeleton
		// clears (loading → false) the chips render, so (re)attach tooltips —
		// this covers the case where mcClient loaded before clientOptions.
		loading: function(val) {
			if (!val) this.initFeatureTooltips()
		},
		// Vue Router reuse-uje istu komponentu kad se menja samo :id param
		// (npr. navigacija sa upgrade na live klijenta), pa created() ne fire-uje ponovo.
		// Watcher resetuje state i fetchuje nove podatke.
		'$route.params.id': function (newId, oldId) {
			if (newId && newId !== oldId) {
				this.upgradeStatus = null
				this.client = {}
				this.mcClient = null
				this.getClient()
			}
		},
	},
	mixins: [juniperUserMixin],
}
</script>

<style scoped>
/* ── Page Header ───────────────────────────────────────────────────────────── */
#editClient {
	padding: 0 0 60px;
}

/* ADA badges next to the client name — same circles as the clients table */
.ce-name-row {
	display: flex;
	align-items: center;
	gap: 10px;
}

.ce-ada-badges {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
}

.ce-ada-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	height: 24px;
	padding: 0 6px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.ce-ada-badges:hover .ce-ada-badge {
	transform: translateY(-1px);
	box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
}

.ce-ada-badge--ok {
	background-color: #dcfce7;
	color: #16a34a;
	font-size: 10px;
}

.ce-ada-badge--error {
	background-color: #dc2626;
	color: #ffffff;
}

.ce-ada-badge--warning {
	background-color: #2563eb;
	color: #ffffff;
}

/* Launch — same pill design as the header quick-actions strip, in green */
.ce-launch-pill {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 12px;
	border: none;
	border-radius: 999px;
	background: #16a34a;
	color: #ffffff;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s ease;
	white-space: nowrap;
}

.ce-launch-pill:hover:not(:disabled) {
	background: #15803d;
}

.ce-launch-pill:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.ce-launch-pill svg {
	font-size: 12px;
}

.ce-alert-panel {
	background: #fff;
	border-radius: 14px;
	padding: 10px 12px;
	border: 1px solid #e5e7eb;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

/* ── Setup alert toasts — fixed bottom-right, auto-dismiss ─────────────────── */
.ce-toast-stack {
	position: fixed;
	right: 20px;
	bottom: 20px;
	z-index: 1090;
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 380px;
	max-width: calc(100vw - 40px);
	pointer-events: none;
}
.ce-toast-stack .ce-alert-panel {
	pointer-events: auto;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
}
/* Slide-in / fade transition for each toast */
.ce-toast-enter-active,
.ce-toast-leave-active {
	transition: transform 0.3s ease, opacity 0.3s ease;
}
.ce-toast-enter,
.ce-toast-leave-to {
	opacity: 0;
	transform: translateX(24px);
}

.ce-alert-panel--danger {
	border-color: #fecaca;
	background: linear-gradient(135deg, #fef2f2 0%, #fff 100%);
}

.ce-alert-panel--warning {
	border-color: #fde68a;
	background: linear-gradient(135deg, #fffbeb 0%, #fff 100%);
}

.ce-alert-panel__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.ce-alert-panel__title-wrap {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	font-weight: 700;
	flex-wrap: wrap;
}

.ce-alert-panel--danger .ce-alert-panel__title-wrap {
	color: #991b1b;
}

.ce-alert-panel--warning .ce-alert-panel__title-wrap {
	color: #92400e;
}

.ce-alert-panel__title {
	line-height: 1.2;
}

.ce-alert-panel__settings-link {
	margin-left: 6px;
	font-size: 11px;
	font-weight: 600;
	color: #1d4ed8;
	text-decoration: none;
}

.ce-alert-panel__settings-link:hover {
	text-decoration: underline;
}

.ce-alert-panel__close {
	border: 0;
	background: rgba(17, 24, 39, 0.06);
	color: #111827;
	font-size: 18px;
	font-weight: 700;
	line-height: 1;
	padding: 0 7px 2px;
	border-radius: 6px;
	cursor: pointer;
}

.ce-alert-panel__close:hover {
	background: rgba(17, 24, 39, 0.14);
}

.ce-alert-panel__list {
	margin: 8px 0 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.ce-alert-panel__list li {
	display: flex;
	flex-direction: column;
	gap: 1px;
	padding: 6px 8px;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.7);
}

.ce-alert-panel__list li strong {
	font-size: 12px;
	line-height: 1.3;
}

.ce-alert-panel__list li span {
	font-size: 11px;
	color: #4b5563;
	line-height: 1.3;
}


.ce-title-skeleton {
	width: 200px;
	height: 24px;
	background: #e5e7eb;
	border-radius: 6px;
	animation: pulse 1.2s infinite;
}
@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.5 } }

.ce-subtitle {
	font-size: 13px;
	color: #6b7280;
	margin: 0;
}

.ce-header-right {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

/* ── Buttons ───────────────────────────────────────────────────────────────── */
.ce-btn {
	display: inline-flex;
	align-items: center;
	padding: 7px 14px;
	border-radius: 7px;
	font-size: 13px;
	font-weight: 500;
	border: 1px solid #e5e7eb;
	background: #fff;
	color: #374151;
	cursor: pointer;
	text-decoration: none;
	transition: background 0.15s, border-color 0.15s;
	white-space: nowrap;
}
.ce-btn:hover { background: #f3f4f6; text-decoration: none; color: #111827; }
.ce-btn--success { background: #166534; color: #fff; border-color: #166534; }
.ce-btn--success:hover { background: #14532d; color: #fff; }
.ce-btn--danger { background: #fff; color: #dc2626; border-color: #fca5a5; }
.ce-btn--danger:hover { background: #fef2f2; }
.ce-btn--sm { font-size: 12px; padding: 4px 10px; }
.ce-btn--icon { padding: 7px 10px; }

/* ── Status Pills ──────────────────────────────────────────────────────────── */
.ce-status-pill {
	display: inline-flex;
	align-items: center;
	padding: 4px 10px;
	border-radius: 20px;
	font-size: 12px;
	font-weight: 600;
}
.ce-status-pill--green  { background: #dcfce7; color: #166534; }
.ce-status-pill--blue   { background: #dbeafe; color: #1e40af; }
.ce-status-pill--orange { background: #fef3c7; color: #92400e; }
.ce-status-pill--purple { background: #ede9fe; color: #5b21b6; }
.ce-status-pill--muted  { background: #f3f4f6; color: #6b7280; }

/* Dashboard flag — sits next to the last-edited chip */
.ce-dash-badge {
	display: inline-flex;
	align-items: center;
	height: 28px;
	padding: 0 10px;
	border-radius: 8px;
	font-size: 12px;
	font-weight: 600;
	cursor: default;
}
.ce-dash-badge--unified { background: #dbeafe; color: #1e40af; }
.ce-dash-badge--old     { background: #f3f4f6; color: #6b7280; }

/* ── Container shell ───────────────────────────────────────────────────────── */
/* Centered Bootstrap container; the map hero deliberately spills wider than this. */
.ce-shell {
	position: relative;
	z-index: 2;
}
/* Content shell is pulled up so the white card tucks into the bottom of the hero. */
.ce-body {
	margin-top: -64px;
}

/* ── Map Hero — full-width banner spanning the whole container ─────────────── */
.ce-hero {
	position: relative;
	height: 320px;
	/* Break out of the parent .container-fluid horizontal padding so the map */
	/* runs edge-to-edge, wider than the centered content container. */
	margin: 0 -12px 0;
	overflow: hidden;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
}
.ce-hero__map {
	width: 100%;
	height: 100%;
}
/* Soft fade at the bottom so the lifted white card blends into the map. */
.ce-hero__fade {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 130px;
	background: linear-gradient(to bottom, rgba(244, 246, 248, 0) 0%, rgba(244, 246, 248, 0.6) 100%);
	pointer-events: none;
}
.ce-hero--skeleton {
	background: #e5e7eb;
}

/* ── Map overlays (population + MC2 link sit on the map) ───────────────────── */
.ce-map-overlay {
	position: absolute;
	z-index: 20;
	background: rgba(255, 255, 255, 0.92);
	backdrop-filter: blur(4px);
	border-radius: 999px;
	padding: 5px 14px;
	font-size: 13px;
	font-weight: 500;
	color: #374151;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	text-decoration: none;
	white-space: nowrap;
}
.ce-map-overlay--top-left {
	top: 16px;
	left: 16px;
}
.ce-map-overlay--top-left:hover {
	background: #fff;
	color: #1d4ed8;
}
.ce-map-overlay--top-right {
	top: 16px;
	right: 16px;
}

/* ── Info Grid (now a single full-width column) ────────────────────────────── */
.ce-info-grid {
	display: block;
	margin-bottom: 16px;
}

/* ── Cards ─────────────────────────────────────────────────────────────────── */
.ce-card {
	background: #fff;
	border-radius: 20px;
	padding: 20px;
	box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
	display: flex;
	flex-direction: column;
	gap: 12px;
}
/* Animated 2px gradient border that slowly rotates around the card. */
@property --ce-angle {
	syntax: '<angle>';
	initial-value: 0deg;
	inherits: false;
}
/* Lifted client card floats over the bottom of the hero. */
.ce-card--lifted {
	position: relative;
	z-index: 3;
	border: 2px solid transparent;
	background:
		linear-gradient(#fff, #fff) padding-box,
		conic-gradient(from var(--ce-angle), var(--ce-border-c1), var(--ce-border-c2), var(--ce-border-c3), var(--ce-border-c2), var(--ce-border-c1)) border-box;
	box-shadow: 0 12px 36px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06);
	animation: ce-border-spin 6s linear infinite;
	/* default (neutral) palette */
	--ce-border-c1: #cbd5e1;
	--ce-border-c2: #94a3b8;
	--ce-border-c3: #e2e8f0;
}
/* Status-coloured border: live → green, staging → blue, etc. */
.ce-card--border-live {
	--ce-border-c1: #16a34a;
	--ce-border-c2: #4ade80;
	--ce-border-c3: #15803d;
}
.ce-card--border-staging {
	--ce-border-c1: #2563eb;
	--ce-border-c2: #60a5fa;
	--ce-border-c3: #1d4ed8;
}
.ce-card--border-upgrade {
	--ce-border-c1: #7c3aed;
	--ce-border-c2: #c4b5fd;
	--ce-border-c3: #6d28d9;
}
.ce-card--border-maintenance {
	--ce-border-c1: #ea580c;
	--ce-border-c2: #fdba74;
	--ce-border-c3: #c2410c;
}
@keyframes ce-border-spin {
	to { --ce-angle: 360deg; }
}
@media (prefers-reduced-motion: reduce) {
	.ce-card--lifted { animation: none; }
}
.ce-card--status {
	gap: 12px;
}

/* ── Client Hero ───────────────────────────────────────────────────────────── */
.ce-client-hero {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 14px;
}
.ce-hero-left {
	display: flex;
	gap: 14px;
	align-items: center;
}
.ce-hero-right {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
}
/* MC2 feature chips (WGS / Facebook / HeyGov modules) */
.ce-features {
	display: inline-flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 5px;
	padding-right: 8px;
	margin-right: 2px;
	border-right: 1px solid #eef0f2;
}
.ce-feat {
	width: 28px;
	height: 28px;
	border-radius: 8px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 13px;
	cursor: default;
}
/* Last-edited chip — neutral gray, sits before the feature icons */
.ce-feat--activity { background: #f3f4f6; color: #6b7280; }
/* WGS — white glove: white icon on a light blue background */
.ce-feat--wgs { background: #60a5fa; color: #ffffff; }
/* Facebook & other non-HeyGov features — bluish */
.ce-feat--fb  { background: #e7f0fe; color: #1877f2; }
/* HeyGov modules — yellowish */
.ce-feat--hg  { background: #fef3c7; color: #b45309; }
.ce-logo-wrap {
	position: relative;
	flex-shrink: 0;
}
.ce-logo-img {
	width: 72px;
	height: 72px;
	object-fit: contain;
	border-radius: 16px;
}
.ce-logo-placeholder {
	width: 72px;
	height: 72px;
	border: none;
	border-radius: 16px;
	background: #f3f4f6;
	color: #6b7280;
	font-size: 12px;
	cursor: pointer;
}
.ce-logo-remove {
	position: absolute;
	top: -8px;
	right: -8px;
	width: 20px;
	height: 20px;
	background: #ef4444;
	color: #fff;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 9px;
	cursor: pointer;
	text-decoration: none;
	opacity: 0;
	transform: scale(0.8);
	transition: opacity 0.15s ease, transform 0.15s ease;
	pointer-events: none;
}
.ce-logo-wrap:hover .ce-logo-remove {
	opacity: 1;
	transform: scale(1);
	pointer-events: auto;
}
.ce-client-name {
	font-size: 21px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 4px;
	cursor: pointer;
}
.ce-client-name--live        { color: #166534; }
.ce-client-name--staging     { color: #1e40af; }
.ce-client-name--upgrade     { color: #5b21b6; }
.ce-client-name--maintenance { color: #92400e; }
.ce-client-name:hover .ce-edit-icon { opacity: 1; }
.ce-edit-icon {
	font-size: 11px;
	color: #9ca3af;
	opacity: 0;
	margin-left: 6px;
	transition: opacity 0.15s;
}
.ce-meta {
	font-size: 12px;
	color: #6b7280;
	margin: 0;
}

/* ── Links Row ─────────────────────────────────────────────────────────────── */
.ce-links-row {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 5px;
}
.ce-link {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	max-width: 100%;
	font-size: 13px;
	color: #1d4ed8;
	text-decoration: none;
}
.ce-link:hover .ce-link__url { text-decoration: underline; }
/* Tinted circular icon badge in front of the link. */
.ce-link__ic {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: #dbe7fe;
	color: #1d4ed8;
	font-size: 11px;
	flex-shrink: 0;
}
.ce-link__url {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.ce-link--muted { color: #6b7280; }
.ce-link--muted .ce-link__ic {
	background: #e7e9ee;
	color: #6b7280;
}

.ce-staging-row {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	max-width: 100%;
}
.ce-migrate-btn {
	display: inline-flex;
	align-items: center;
	font-size: 11px;
	font-weight: 600;
	line-height: 1;
	padding: 5px 11px;
	border: 1px solid #c7d2fe;
	background: #eef2ff;
	color: #4338ca;
	border-radius: 999px;
	cursor: pointer;
	letter-spacing: 0.2px;
	transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
	flex-shrink: 0;
}
.ce-migrate-btn:hover:not(:disabled) { background: #4338ca; color: #fff; border-color: #4338ca; }
.ce-migrate-btn:disabled { opacity: 0.5; cursor: default; }

/* ── Migrate Staging URL Modal ─────────────────────────────────────────────── */
.migrate-summary {
	display: flex;
	flex-direction: column;
	gap: 6px;
	background: #f8f9fa;
	border-radius: 6px;
	padding: 10px 12px;
}
.migrate-summary__row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}
.migrate-summary__label {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.3px;
	color: #6b7280;
	white-space: nowrap;
}
.migrate-summary__value {
	font-size: 13px;
	text-align: right;
	word-break: break-all;
}
.migrate-steps {
	padding-left: 1.2em;
	margin-bottom: 0;
}
.migrate-steps li { margin-bottom: 4px; }

/* ── Card body — horizontal band: links/last-static on the left, stats right ── */
/* ── Card meta row: links on the left, last-static on the right ────────────── */
.ce-card-body {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 24px;
	flex-wrap: wrap;
}
.ce-card-info {
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-width: 0;
	flex: 1 1 280px;
}

/* ── Hover-only delete button, overflowing the top-right corner ────────────── */
.ce-card-delete {
	position: absolute;
	top: -14px;
	right: -14px;
	width: 38px;
	height: 38px;
	border: none;
	border-radius: 50%;
	background: #ef4444;
	color: #fff;
	font-size: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 18px rgba(239, 68, 68, 0.45);
	cursor: pointer;
	opacity: 0;
	transform: scale(0.6);
	transition: opacity 0.18s ease, transform 0.18s ease, background 0.15s ease;
	pointer-events: none;
	z-index: 5;
}
.ce-card--lifted:hover .ce-card-delete {
	opacity: 1;
	transform: scale(1);
	pointer-events: auto;
}
.ce-card-delete:hover {
	background: #dc2626;
	transform: scale(1.08);
}

/* ── Stats Row — compact watermark chips, right-aligned ────────────────────── */
.ce-stats-row {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	justify-content: flex-end;
}
.ce-stat-chip {
	position: relative;
	overflow: hidden;
	background: #f9fafb;
	border: 1px solid #eef0f2;
	border-radius: 13px;
	padding: 8px 10px;
	width: 72px;
	flex: 0 0 auto;
}
/* Icon as a large, faded watermark pushed to the right edge; zooms on hover. */
.ce-stat-chip__bg {
	position: absolute;
	right: -8px;
	top: 50%;
	transform: translateY(-50%);
	transform-origin: center right;
	font-size: 32px;
	opacity: 0.18;
	transition: transform 0.25s ease, opacity 0.25s ease;
	pointer-events: none;
}
.ce-stat-chip--link {
	display: block;
	text-decoration: none;
	cursor: pointer;
	transition: border-color 0.15s ease, background 0.15s ease;
}
.ce-stat-chip--link:hover {
	border-color: #c7d2fe;
	background: #f3f5f9;
}
.ce-stat-chip:hover .ce-stat-chip__bg {
	transform: translateY(-50%) scale(1.25);
	opacity: 0.28;
}
.ce-stat-chip__body {
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
}
.ce-stat-chip-value {
	font-size: 16px;
	font-weight: 700;
	color: #111827;
	line-height: 1;
}
.ce-stat-chip-label {
	font-size: 9px;
	color: #6b7280;
	margin-top: 3px;
	white-space: nowrap;
}
/* Two-line label with a tight line-height (e.g. "Business Directory"). */
.ce-stat-chip-label--multi {
	white-space: normal;
	line-height: 0.95;
}
/* Watermark icon colours (saturated glyph, faded by opacity above). */
.ce-stat-chip__bg--blue   { color: #1565C0; }
.ce-stat-chip__bg--teal   { color: #0f766e; }
.ce-stat-chip__bg--orange { color: #c2410c; }
.ce-stat-chip__bg--pink   { color: #be185d; }
.ce-stat-chip__bg--green  { color: #15803d; }
.ce-stat-chip__bg--purple { color: #6d28d9; }
.ce-stat-chip__bg--indigo { color: #4338ca; }
.ce-stat-chip__bg--cyan   { color: #0e7490; }
.ce-stat-chip__bg--red    { color: #b91c1c; }

.ce-backup-row, .ce-theme-row {
	font-size: 12px;
	color: #6b7280;
	display: flex;
	align-items: center;
	gap: 8px;
	text-decoration: none;
}
.ce-backup-row:hover {
	color: #374151;
	text-decoration: none;
}
.ce-backup-row:hover .ce-backup-icon {
	opacity: 0.9;
}
/* Two-tone hammer badge for the "Last static" → deployments link. */
.ce-backup-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	border-radius: 8px;
	background: #ede9fe;
	color: #6d28d9;
	font-size: 12px;
	flex-shrink: 0;
	transition: opacity 0.15s ease;
}
.ce-theme-badge {
	display: inline-flex;
	align-items: center;
	background: #f3f4f6;
	color: #4b5563;
	padding: 6px 12px;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	text-decoration: none;
	transition: background 0.15s ease, color 0.15s ease;
}
.ce-theme-badge:hover {
	background: #e5e7eb;
	color: #1f2937;
	text-decoration: none;
}
.ce-pm-badge {
	display: inline-flex;
	align-items: center;
	background: #f3f4f6;
	color: #4b5563;
	padding: 6px 12px;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	cursor: default;
}

/* ── Status Bar ────────────────────────────────────────────────────────────── */

.ce-status-pill {
	display: inline-flex;
	align-items: center;
	font-size: 14px;
	font-weight: 700;
	padding: 6px 14px;
	border-radius: 999px;
	letter-spacing: 0.3px;
}
.ce-status-pill--green { background: #d1fae5; color: #065f46; }
.ce-status-pill--blue  { background: #dbeafe; color: #1e40af; }
.ce-status-pill--orange { background: #ffedd5; color: #9a3412; }

/* Pulsing "live" indicator dot. */
.ce-live-dot {
	animation: ce-live-pulse 1.6s ease-in-out infinite;
	transform-origin: center;
}
@keyframes ce-live-pulse {
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.35; transform: scale(0.7); }
}
@media (prefers-reduced-motion: reduce) {
	.ce-live-dot { animation: none; }
}

.ce-maintenance-inline {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	font-size: 13px;
	color: #6b7280;
	padding: 4px 10px;
	border-radius: 8px;
	border: 1px solid #e5e7eb;
	transition: border-color 0.2s, background 0.2s;
}
.ce-maintenance-inline.is-on {
	border-color: #f59e0b;
	background: #fffbeb;
	color: #92400e;
}

/* ── Section Headers ───────────────────────────────────────────────────────── */
.ce-section-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
}
.ce-section-title {
	font-size: 13px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #6b7280;
}
.ce-section-title--danger {
	color: #b91c1c;
}
.ce-section-subtitle {
	font-size: 12px;
	color: #9ca3af;
	margin-top: 2px;
}

/* ── Sections — newspaper columns so small groups fill the width ───────────── */
/* Separate card that wraps all the action sections. */
.ce-sections-card {
	margin-top: 4px;
	box-shadow: 0 12px 36px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06);
}

/* ── Sections — all four groups in one row ─────────────────────────────────── */
.ce-sections {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 24px 32px;
	align-items: start;
}
@media (max-width: 1100px) {
	.ce-sections { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
	.ce-sections { grid-template-columns: 1fr; }
}
.ce-section { min-width: 0; }
.ce-section .ce-section-header { margin-bottom: 12px; }

/* ── Actions — compact horizontal rows (list style) ────────────────────────── */
.ce-actions-grid {
	display: flex;
	flex-direction: column;
	gap: 8px;
}
.ce-action-card {
	display: flex;
	align-items: center;
	gap: 12px;
	background: #fff;
	border: 1px solid #eef0f2;
	border-radius: 14px;
	padding: 11px 13px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
	text-decoration: none;
	transition: box-shadow 0.15s, transform 0.12s, border-color 0.15s;
	position: relative;
}
.ce-action-card:hover {
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
	transform: translateY(-1px);
	border-color: #dbe3ee;
	text-decoration: none;
}
.ce-action-card--disabled {
	opacity: 0.5;
	pointer-events: none;
}

.ce-action-icon {
	width: 40px;
	height: 40px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	flex-shrink: 0;
}
.ce-action-icon--blue   { background: #dbeafe; color: #1565C0; }
.ce-action-icon--purple { background: #ede9fe; color: #6d28d9; }
.ce-action-icon--teal   { background: #ccfbf1; color: #0f766e; }
.ce-action-icon--indigo { background: #e0e7ff; color: #4338ca; }
.ce-action-icon--green  { background: #dcfce7; color: #166534; }
.ce-action-icon--orange { background: #ffedd5; color: #c2410c; }
.ce-action-icon--red    { background: #fee2e2; color: #dc2626; }
.ce-action-icon--gray   { background: #f3f4f6; color: #4b5563; }
.ce-action-icon--cyan   { background: #cffafe; color: #0e7490; }
.ce-action-icon--pink   { background: #fce7f3; color: #be185d; }

.ce-action-card__text {
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 1;
}
.ce-action-label {
	font-size: 13px;
	font-weight: 600;
	color: #111827;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.ce-action-desc {
	font-size: 11px;
	color: #9ca3af;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-top: 1px;
}
.ce-action-card__arrow {
	font-size: 11px;
	color: #cbd5e1;
	opacity: 0;
	flex-shrink: 0;
	transition: opacity 0.15s ease, transform 0.15s ease;
}
.ce-action-card:hover .ce-action-card__arrow {
	opacity: 1;
	transform: translateX(2px);
}
.ce-action-disabled-label {
	font-size: 10px;
	color: #9ca3af;
}
.ce-action-badge {
	position: absolute;
	top: 6px;
	right: 6px;
	font-size: 9px;
	font-weight: 600;
	padding: 2px 5px;
	border-radius: 6px;
}
.ce-action-badge--green  { background: #dcfce7; color: #166534; }
.ce-action-badge--gray   { background: #e5e7eb; color: #374151; }
.ce-action-badge--orange { background: #fef3c7; color: #92400e; }

/* === Delete Modal — matches upgrade-modal styling, danger-themed === */
.delete-modal {
	border: none;
	border-radius: 16px;
	overflow: hidden;
}
.delete-modal__header {
	background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
	border-bottom: 1px solid #fecaca;
	padding: 18px 24px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
}
.delete-modal__header-icon {
	width: 44px;
	height: 44px;
	border-radius: 10px;
	background: rgba(255, 255, 255, 0.85);
	color: #b91c1c;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	margin-right: 14px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.delete-modal__body {
	padding: 24px;
}
.delete-modal__footer {
	background: #f9fafb;
	border-top: 1px solid #e5e7eb;
	padding: 14px 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
}
.delete-modal__footer-actions {
	display: flex;
	gap: 8px;
	margin-left: auto;
}
.delete-progress {
	display: flex;
	align-items: center;
	font-size: 13px;
	color: #4b5563;
	min-width: 0;
}

/* Summary rows */
.delete-summary {
	background: #f9fafb;
	border-radius: 10px;
	padding: 14px 18px;
}
.delete-summary__row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 6px 0;
	border-bottom: 1px solid #e5e7eb;
	gap: 12px;
}
.delete-summary__row:last-child {
	border-bottom: none;
}
.delete-summary__label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	flex-shrink: 0;
}
.delete-summary__value {
	font-size: 14px;
	color: #111827;
	text-align: right;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.delete-summary__link {
	color: #1d4ed8;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.delete-summary__link:hover {
	text-decoration: underline;
}

/* Stat grid */
.delete-stat-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 10px;
}
@media (max-width: 540px) {
	.delete-stat-grid { grid-template-columns: 1fr 1fr; }
}
.delete-stat {
	display: flex;
	gap: 12px;
	align-items: center;
	padding: 10px 12px;
	background: #fff7ed;
	border: 1px solid #fed7aa;
	border-radius: 10px;
}
.delete-stat__icon {
	font-size: 18px;
	color: #c2410c;
	flex-shrink: 0;
}
.delete-stat__value {
	font-size: 18px;
	font-weight: 700;
	color: #111827;
	line-height: 1;
}
.delete-stat__label {
	font-size: 11px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin-top: 2px;
}

/* Type-to-confirm */
.delete-confirm code {
	background: #fee2e2;
	color: #991b1b;
	padding: 1px 6px;
	border-radius: 4px;
	font-weight: 600;
}
.delete-confirm .form-control:focus {
	border-color: #fca5a5;
	box-shadow: 0 0 0 0.2rem rgba(220, 38, 38, 0.15);
}

/* ── Skeleton Loader ──────────────────────────────────────────────────────── */
@keyframes ce-skeleton-shimmer {
	0%   { background-position: -400px 0; }
	100% { background-position: 400px 0; }
}
.ce-skeleton-shimmer {
	background-color: #eef0f3;
	background-image: linear-gradient(
		90deg,
		rgba(238, 240, 243, 0) 0%,
		rgba(255, 255, 255, 0.75) 50%,
		rgba(238, 240, 243, 0) 100%
	);
	background-size: 400px 100%;
	background-repeat: no-repeat;
	animation: ce-skeleton-shimmer 1.4s linear infinite;
	border-radius: 6px;
}

/* Skeleton card uses the same .ce-card base so dimensions match exactly */
.ce-skeleton__card {
	gap: 14px;
}

/* Hero row (logo + name + status pill) */
.ce-skeleton__hero {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 14px;
}
.ce-skeleton__hero-left {
	display: flex;
	gap: 14px;
	align-items: flex-start;
	flex: 1;
	min-width: 0;
}
.ce-skeleton__hero-right {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 6px;
	flex-shrink: 0;
}
.ce-skeleton__logo {
	width: 72px;
	height: 72px;
	border-radius: 10px;
	flex-shrink: 0;
}
.ce-skeleton__hero-text {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding-top: 6px;
	flex: 1;
	min-width: 0;
}
.ce-skeleton__pill {
	width: 70px;
	height: 22px;
	border-radius: 999px;
}
.ce-skeleton__badge {
	width: 90px;
	height: 16px;
	border-radius: 4px;
}

/* Links + stats + backup line */
.ce-skeleton__links {
	display: flex;
	flex-direction: column;
	gap: 8px;
}
.ce-skeleton__stats {
	display: flex;
	gap: 10px;
	flex-wrap: wrap;
}
.ce-skeleton__stat {
	width: 90px;
	height: 56px;
	border-radius: 16px;
}

/* Map placeholder */
.ce-skeleton__map {
	min-height: 240px;
	border-radius: 12px;
}

/* Lines */
.ce-skeleton-line {
	display: block;
	height: 12px;
	width: 100%;
}
.ce-skeleton-line--title {
	height: 20px;
	width: 60%;
}
.ce-skeleton-line--sub {
	height: 12px;
	width: 40%;
}
.ce-skeleton-line--link {
	height: 14px;
	width: 75%;
}
.ce-skeleton-line--backup {
	height: 12px;
	width: 50%;
}
.ce-skeleton-line--section-title {
	height: 14px;
	width: 180px;
}
.ce-skeleton-line--section-sub {
	height: 10px;
	width: 240px;
	margin-top: 6px;
}
.ce-skeleton-line--action {
	height: 10px;
	width: 60%;
}

/* Action card skeleton — reuses .ce-action-card for identical dims */
.ce-skeleton__action {
	cursor: default;
	pointer-events: none;
}
.ce-skeleton__action:hover {
	transform: none;
	box-shadow: 0 1px 3px rgba(0,0,0,.06);
	border-color: transparent;
}
.ce-skeleton__action-icon {
	width: 40px;
	height: 40px;
	border-radius: 10px;
}

/* Two-tone icon badge for modal headers (e.g. Launch). */
.ce-modal-icon {
	width: 40px;
	height: 40px;
	border-radius: 11px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 17px;
	flex-shrink: 0;
}
.ce-modal-icon--green { background: #dcfce7; color: #16a34a; }
</style>
